/**
 * Minimal RFC 5322 / MIME message parser.
 *
 * Handles the subset of the standards needed to decode real-world email:
 *  - RFC 5322: header syntax, header folding, body separator.
 *  - RFC 2045: Content-Type, Content-Transfer-Encoding.
 *  - RFC 2046: multipart/* boundary delimiting.
 *  - RFC 2047: encoded-word header values (=?UTF-8?B?...?=).
 *  - RFC 2183: Content-Disposition (attachment / inline / filename).
 *
 * Raw decoded bytes are preserved on every leaf part so binary attachments
 * (PDF, images, zips, …) round-trip byte-for-byte.
 *
 * Zero runtime deps; runs in any modern browser.
 */

import { unfoldFormatFlowed } from "./derivations/format-flowed";
import { type StageEmitter } from "./loading/stages";
import { EmailParseError } from "./parse-error";
import { DEFECT } from "./parser-core/defect-codes";
import {
  createDefectCollector,
  type Defect,
  type DefectCollector,
} from "./parser-core/defects";
import {
  ADDRESSES_LAYER_VERSION,
  ARTIFACT_LAYER_VERSION,
  ATTACHMENTS_LAYER_VERSION,
  BODIES_LAYER_VERSION,
  EMBEDDED_MESSAGES_LAYER_VERSION,
  ENVELOPE_LAYER_VERSION,
  HEADERS_LAYER_VERSION,
  IDENTITY_LAYER_VERSION,
  MIME_LAYER_VERSION,
  RAW_LAYER_VERSION,
  SYNTAX_LAYER_VERSION,
  THREADING_LAYER_VERSION,
  TIME_LAYER_VERSION,
  type AddressLayer,
  type ArtifactLayer,
  type AttachmentLayer,
  type BodyLayer,
  type EmbeddedMessage,
  type EmbeddedMessageLayer,
  type EnvelopeLayer,
  type HeaderLayer,
  type IdentityLayer,
  type MimeLayer,
  type RawLayer,
  type SyntaxLayer,
  type ThreadLayer,
  type TimeLayer,
} from "./parser-core/layers";
import { PARSED_EMAIL_SCHEMA_VERSION } from "./parser-core/parsed-email";
import {
  DEFAULT_PARSER_CONFIG,
  readParserConfig,
  type ParserConfig,
} from "./perf/budgets";
import {
  bucketForBytes,
  createStageMeasure,
  recordPerfSample,
  type PerfFixtureBucket,
  type StageMeasure,
  type StageMeasureResult,
} from "./perf/measure";
import { bytesToBinaryString, byteStringToBytes } from "./utils/buffer";

export { EmailParseError } from "./parse-error";
export type { EmailParseErrorFields } from "./parse-error";

/** One header line with original casing preserved (duplicates allowed). */
export interface HeaderEntry {
  /** Original casing as it appeared in the source. */
  name: string;
  /** Unfolded value (continuation lines joined, trimmed). Not decoded. */
  value: string;
}

/** Parsed top-level headers — both an ordered list and a lookup map. */
export interface ParsedHeaders {
  /** Lowercase name → value. Convenient for lookup; duplicates are joined. */
  raw: Record<string, string>;
  /** Every header in original order + original casing (duplicates preserved). */
  all: HeaderEntry[];
  from?: string;
  to?: string;
  cc?: string;
  subject?: string;
  date?: string;
  messageId?: string;
}

/** A single MIME part — leaf or container — produced by {@link parseEmail}. */
export interface EmailPart {
  headers: Record<string, string>;
  headerList: HeaderEntry[];
  contentType: string;
  charset: string;
  transferEncoding: string;
  disposition?: string;
  filename?: string;
  contentId?: string;
  /**
   * RFC 2646 / RFC 3676 `format` Content-Type parameter (e.g. `"flowed"`),
   * lower-cased. `contentType` carries only the bare media type with all
   * parameters stripped, so the flowed-body decoder reads the parameter from
   * here instead of re-parsing the media type (which never carried it).
   */
  format?: string;
  /** RFC 3676 `delsp` Content-Type parameter (e.g. `"yes"`), lower-cased. */
  delsp?: string;
  /** Decoded body as text, populated only for text/* parts. */
  body: string;
  /** Decoded raw bytes. Empty on multipart container nodes. */
  bytes: Uint8Array;
  rawBody: string;
  parts: EmailPart[];
  /**
   * True when this container's sub-parts were NOT parsed because the
   * `maxMimePartCount` budget was already spent. `parts` is empty and the
   * message carries more structure than the tree shows. A consumer that
   * renders "no sub-parts" must say "not read" instead.
   */
  partsTruncated?: boolean;
  /**
   * True when `bytes` was cut to `maxDecodedPartBytes` / `maxAttachmentBytes`.
   * The decoded payload is deliberately incomplete; a hash or an extractor
   * reading it is reading a prefix, not the part.
   */
  bytesTruncated?: boolean;
  /**
   * Addressable citation of this part inside the original source message.
   *
   * `partId` — dotted-path id matching the convention used by the MimeTree UI
   * + indicators (`"1"`, `"1.2"`, …). 1-based; the root part is `"1"`. Always
   * present; pre-fix the id only existed inside the views and indicator
   * derivation but never on the part itself.
   *
   * `partHeaderByteRange` — `[start, end)` byte offsets inside the raw source
   * string for the part's header block (up to and including the blank line
   * that terminates it). When a chip cites this part by partId, the receiver
   * can use this range to scroll Raw to the exact bytes that describe the
   * part's content-type / disposition / filename.
   *
   * `partBodyByteRange` — `[start, end)` byte offsets for the part's body.
   * For multipart containers this includes the body of the container (the
   * boundary block). For leaves it is the encoded body bytes BEFORE Base64/QP
   * decoding so the offsets match the source string a permalink consumer is
   * rendering. Both ranges are undefined when the parser couldn't recover the
   * offsets (e.g. top-level container synthesised after a depth-cap trip).
   */
  partId?: string;
  partHeaderByteRange?: [number, number];
  partBodyByteRange?: [number, number];
}

/** A single file attachment extracted from the parsed MIME tree. */
export interface Attachment {
  filename: string;
  contentType: string;
  size: number;
  bytes: Uint8Array;
  disposition: "attachment" | "inline";
  contentId?: string;
  /**
   * Dotted-path id of the MIME part this attachment was extracted from.
   * Mirrors `EmailPart.partId` so the AttachmentsList chip can cite the same
   * node the MimeTree surfaces. Absent only when an upstream caller
   * synthesised an Attachment outside the parser (e.g. tests).
   */
  partId?: string;
  /**
   * Byte offsets of the attachment's MIME part header block inside the
   * normalized source. Pre-fix AttachmentsList's chip carried `byteRange: [0,
   * a.size]` which pointed at the attachment data length, not the source
   * bytes — the resulting `#evidence=byte-…` id was meaningless. The chip now
   * uses these offsets so a click lands on the part-boundary bytes via
   * `byteOffsetToLine`.
   */
  partHeaderByteRange?: [number, number];
  partBodyByteRange?: [number, number];
}

/**
 * Top-level result of {@link parseEmail} — ready for the analysis layer.
 *
 * The shape is *additive*: the legacy flat fields (`headers`, `root`, `html`,
 * `text`, `attachments`, `inlineParts`, `defects`) keep their exact pre-Q1
 * paths so every existing consumer continues to work without code change.
 * Alongside them, the layered fields from v3 §5.1 / v4 §06 are populated on
 * every parse so new consumers can reach for `parsed.headers.ordered`
 * (layered) or keep using `parsed.headers.all` (flat). The two views point at
 * the same underlying data — the parser builds them once and exposes both
 * surfaces. Migrating each consumer is a long-tail follow-up (see `// TODO(Q6
 * follow-up)` comments).
 *
 * Per-layer `schemaVersion` lets the JSON export envelope encode each layer
 * independently, so additive layer changes don't bump a global version that
 * would reject older permalinks.
 */
export interface ParsedEmail {
  // ---------- Legacy flat fields (compat shim, do not remove) ----------
  /**
   * Top-level headers. The shape is `ParsedHeaders & HeaderLayer` so
   * legacy consumers keep reading `parsed.headers.all` /
   * `parsed.headers.raw` while new consumers can reach for
   * `parsed.headers.ordered` / `parsed.headers.byName` /
   * `parsed.headers.schemaVersion`.
   */
  headers: ParsedHeadersWithLayer;
  root: EmailPart;
  html: string | null;
  text: string | null;
  attachments: Attachment[];
  /** Content-ID → leaf part, for resolving `cid:` URIs in the HTML body. */
  inlineParts: Record<string, EmailPart>;
  /**
   * Defects accumulated during parsing. A fresh collector is allocated per
   * call so defects never leak across parses. Today this is always `[]`;
   * theme O2 (Commit 4) refactors silent-recovery sites in the derivations to
   * actually emit entries.
   */
  defects: readonly Defect[];

  // ---------- Catalog Q1 layered shape -----------------------
  /**
   * Global envelope version — bumped only when the *root* shape
   * changes (a new layer slot, a removed slot). Per-layer changes
   * bump that layer's own `schemaVersion` so readers can accept
   * older permalinks layer-by-layer.
   */
  schemaVersion: string;
  artifact: ArtifactLayer;
  raw: RawLayer;
  syntax: SyntaxLayer;
  /**
   * `headers` is *both* the legacy ParsedHeaders shape (with `all`,
   * `from`, etc.) AND the layered HeaderLayer (with `ordered`,
   * `byName`). They share field names that don't collide: `from`,
   * `to`, `cc`, `subject`, `date`, `messageId`. The layered-only
   * fields (`ordered`, `byName`, `schemaVersion`) live alongside the
   * legacy `all`, `raw` lookup on the same object.
   */
  // headers also implements HeaderLayer (intersected below)
  addresses: AddressLayer;
  identity: IdentityLayer;
  envelope: EnvelopeLayer;
  threading: ThreadLayer;
  time: TimeLayer;
  mime: MimeLayer;
  bodies: BodyLayer;
  /**
   * `attachments` keeps its legacy array shape (a top-level
   * `Attachment[]`); the layered `attachments.list` view is exposed
   * via the `attachmentsLayer` slot below to avoid a name collision.
   * New consumers should reach for `attachmentsLayer.list`.
   */
  attachmentsLayer: AttachmentLayer;
  embeddedMessages: EmbeddedMessageLayer;
  /**
   * Per-stage wall-clock rollup for the parse pipeline. Always populated; the
   * rolling session sampler picks it up via `recordPerfSample("parse",...)`.
   * The DebugOverlay reads the same rollup off the live message to show
   * last-parse timings.
   *
   * Stages covered here: hash, headers-parse, mime-scan, body-decode,
   * charset-normalise, defects-collect. Analyser-only stages
   * (auth-results-parse, address-parse, threading-derive, spoof-derive,
   * indicators-extract, provenance-stamp) land on the analysis result.
   */
  perf: StageMeasureResult;
}

/**
 * Combined `ParsedHeaders` + `HeaderLayer` shape that `parseEmail` actually
 * returns for the `headers` slot. The two interfaces agree on `from / to / cc
 * / subject / date / messageId`; the layered-only fields (`ordered`,
 * `byName`, `schemaVersion`) extend the legacy flat shape additively.
 */
export interface ParsedHeadersWithLayer extends ParsedHeaders, HeaderLayer {}

// ---------- Public API ----------

/**
 * Parse a raw `.eml` source string into a structured {@link ParsedEmail}.
 *
 * Call this once per message — it walks the entire MIME tree, decodes
 * every part's bytes, and extracts attachments + inline-image lookups.
 * Pure and deterministic: same input always yields the same output.
 */
/**
 * Parse a raw RFC 5322 / MIME message string into a structured `ParsedEmail`.
 *
 * `options.onStage` is the optional catalog C.X2 stage emitter — when
 * present, the parser fires named events as it walks the pipeline so a
 * stage-aware UI can show "Parsing headers…" / "Building MIME tree…"
 * / "Extracting attachments…" without taking a guess at where the
 * pipeline currently is. The events are best-effort timings; production
 * call sites can leave the option off and the parser is unchanged.
 */
export function parseEmail(
  source: string,
  options: {
    onStage?: StageEmitter;
    config?: Partial<ParserConfig>;
    /**
     * Optional fixture bucket override. When omitted, the parser picks one
     * via `bucketForBytes(source.length)`. The bucket drives per-stage budget
     * lookup so a "pathological" 200-part fixture isn't held to the "medium"
     * budget.
     */
    perfBucket?: PerfFixtureBucket;
  } = {},
): ParsedEmail {
  const onStage = options.onStage;
  const t = (stage: Parameters<StageEmitter>[0], started: number) => {
    onStage?.(stage, performance.now() - started);
  };
  // Per-stage wall-clock measurement. The measurer threads through the parse
  // pipeline; each stage records its duration. The result lands on
  // `ParsedEmail.perf` and is pushed into the rolling session sampler so
  // DebugOverlay can show p50 / p95 / p99 across the session.
  const perfBucket = options.perfBucket ?? bucketForBytes(source.length);
  const perf: StageMeasure = createStageMeasure({ bucket: perfBucket });
  // Fresh collector per call. The plumbing is wired now; emit sites (theme
  // O2) populate it commit-by-commit.
  const defects = createDefectCollector();
  // Resolve effective ParserConfig: explicit override wins, otherwise fall
  // back to the persisted Settings → Limits value, otherwise the spec
  // default. Read once per parse so a config change between calls is
  // reflected without re-importing the parser.
  const persisted = options.config ? null : readParserConfig();
  const config: ParserConfig = {
    ...DEFAULT_PARSER_CONFIG,
    ...(persisted ?? {}),
    ...(options.config ?? {}),
  };

  // Message-bytes limit. The defect contract in parser-core/defect-codes.ts
  // says bytes past the limit "are not seen by any subsequent stage", and this
  // is where that becomes true: everything downstream reads `source`, so the
  // cut has to happen before the hash walk, the line-ending canonicalisation
  // and the tree walk, not merely be announced. Before this the parser warned
  // and then scanned the whole thing, which is the opposite of a limit.
  const sourceBytes = source.length;
  if (sourceBytes > config.maxMessageBytes) {
    source = source.slice(0, config.maxMessageBytes);
    defects.push({
      code: DEFECT.MESSAGE_BYTES_EXCEEDED,
      source: "parser",
      severity: "warning",
      message: `Message bytes (${sourceBytes}) exceed maxMessageBytes (${config.maxMessageBytes}).`,
      evidence: { layer: "raw" },
      recovery: `Truncated the source to the limit; ${sourceBytes - config.maxMessageBytes} bytes were not parsed.`,
    });
  }

  // Catalog H stage `hash` — a COST PROBE, not a digest anyone may use.
  // The parser is synchronous and the only real SHA-256 available is the
  // async `crypto.subtle`, so this stage stands in for it: an FNV-1a pass
  // over the same bytes, whose cost tracks a real digest within a small
  // constant factor, so the perf rollup reports what a caller would pay.
  //
  // The result is DISCARDED on purpose (see the `void` below). FNV-1a is not
  // a cryptographic hash; returning it would invite a caller to treat it as
  // a message identity. Digests that identify evidence come from
  // `crypto.subtle.digest`, at the call sites that can await one.
  const endHash = perf.start("hash");
  let fnvHash = 0x811c9dc5;
  for (let i = 0; i < source.length; i++) {
    fnvHash = (fnvHash ^ source.charCodeAt(i)) >>> 0;
    fnvHash =
      (fnvHash +
        ((fnvHash << 1) +
          (fnvHash << 4) +
          (fnvHash << 7) +
          (fnvHash << 8) +
          (fnvHash << 24))) >>>
      0;
  }
  // Touch fnvHash so the optimiser can't elide the loop entirely.
  void fnvHash;
  endHash();

  // Canonicalise line endings to LF. Each terminator collapses to a single
  // `\n`: a CRLF — including a CRLF carrying one or more *stray* leading CRs
  // (`\r\r\n`, a bare CR immediately before a real CRLF) — is ONE line ending,
  // then any remaining lone CR (legacy Mac) becomes `\n`. The `\r+\n` match (vs
  // the naive two-pass `\r\n`→`\n` then `\r`→`\n`) is load-bearing: the old
  // pass turned `tool\r\r\n` into `tool\n\n`, fabricating a blank line that
  // splitHeadersAndBody / the separator probe then took as the FIRST
  // header/body boundary — silently relocating every header after that point
  // (DKIM-Signature, ARC, Authentication-Results, Subject) into the body so the
  // analyst saw "no DKIM / no Authentication-Results". A *genuine* blank line
  // (`\r\n\r\n`) is still two `\r+\n` matches → `\n\n`, so real header/body
  // separators are preserved. The raw bare-CR shape still surfaces
  // RAW_INVALID_LINE_ENDING below for forensic visibility.
  const normalized = source.replace(/\r+\n/g, "\n").replace(/\r/g, "\n");
  // Empty / whitespace-only inputs cannot be a meaningful message.
  // useEmail catches this earlier (showing `errors.needSource`) but a
  // direct caller of parseEmail (tests, future programmatic uses)
  // should get a structured error rather than a near-empty result.
  if (!normalized.trim()) {
    throw new EmailParseError("Source is empty.", {
      reason: "empty-input",
      offset: 0,
    });
  }

  // RAW_ framing defects. Inspect the original `source` bytes (before \r\n /
  // \r normalisation) so legacy / malformed line endings still surface a
  // defect for forensic visibility.
  // RAW_INVALID_LINE_ENDING — source contains bare CR (legacy Mac) or bare LF
  // mixed with CRLF that the normalisation pass already collapsed silently.
  // We detect the *non-canonical* shape on the raw input so the defect fires
  // even though the normalised string looks clean.
  const hasCrlf = source.includes("\r\n");
  const hasBareCr = /\r(?!\n)/.test(source);
  const hasBareLfWithCrlf = hasCrlf && /(?<!\r)\n/.test(source);
  if (hasBareCr || hasBareLfWithCrlf) {
    defects.push({
      code: DEFECT.RAW_INVALID_LINE_ENDING,
      source: "parser",
      severity: "info",
      message: hasBareCr
        ? "Source contains bare CR line endings (legacy Mac) without LF."
        : "Source mixes bare LF with CRLF line endings.",
      evidence: { layer: "raw" },
      recovery: "Normalised CR / mixed endings to LF before parsing.",
    });
  }

  // RAW-002 + RFC5321-001. RFC 5322 §2.1.1 caps text lines at 998 octets
  // (excluding CRLF) and RFC 5321 §4.5.3.1.6 caps SMTP text lines at 1000
  // octets including CRLF. Scan the normalised source once and surface a
  // single info defect when any line exceeds 998 octets — the parser still
  // walks the full input. We measure UTF-8 byte length so multi-byte
  // characters don't inflate false positives via JS string length.
  {
    let firstOver = -1;
    let firstOverBytes = 0;
    let totalOver = 0;
    const lines = normalized.split("\n");
    const encoder = new TextEncoder();
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]!;
      // Quick path: ASCII lines have length === byte length.
      const hasNonAscii = /[\u0080-\uFFFF]/.test(line);
      const probableBytes = hasNonAscii ? encoder.encode(line).length : line.length;
      if (probableBytes > 998) {
        totalOver += 1;
        if (firstOver === -1) {
          firstOver = i;
          firstOverBytes = probableBytes;
        }
      }
    }
    if (firstOver !== -1) {
      defects.push({
        code: DEFECT.RAW_LINE_LENGTH_EXCEEDED,
        source: "parser",
        severity: "info",
        message: `Line ${firstOver + 1} is ${firstOverBytes} octets; RFC 5322 §2.1.1 caps text lines at 998 octets${totalOver > 1 ? ` (${totalOver} lines exceed)` : ""}.`,
        evidence: { layer: "raw", lineRange: [firstOver, firstOver] },
        recovery: "All bytes kept; downstream SMTP relays may refuse the message.",
      });
    }
  }

  // RAW_NO_HEADER_SEPARATOR — no `\n\n` separator between headers and
  // body. Two cases: (a) entire input is treated as headers with an
  // empty body, and (b) the first non-empty line is not `Name: value`.
  // The parser still recovers (splitHeadersAndBody returns body=""),
  // but downstream consumers should know the framing was broken.
  const separatorIdx = normalized.indexOf("\n\n");
  if (separatorIdx === -1) {
    defects.push({
      code: DEFECT.RAW_NO_HEADER_SEPARATOR,
      source: "parser",
      severity: "warning",
      message: "Source has no CRLF CRLF (or LF LF) separator between headers and body.",
      evidence: { layer: "raw" },
      recovery: "Treated the entire input as headers; body extracted as empty.",
    });
  }

  // RAW_TRUNCATED_INPUT — fires when the input ends mid-message in a
  // way that suggests genuine truncation: the file lacks a trailing
  // newline, the body region is non-trivial, and the source ends in
  // the middle of what looks like a MIME boundary marker / opener
  // (a `--` prefix with no matching closer). The MIME_BOUNDARY_UNCLOSED
  // defect catches the structural case from inside the boundary
  // splitter; RAW_TRUNCATED_INPUT is the bytes-level companion that
  // fires when the file's last bytes look like a mid-write cut. We
  // restrict to multipart messages so a plain `text/plain` body
  // without a trailing newline (the common `echo` / cat-output case)
  // does NOT misfire.
  const bodyTail = separatorIdx !== -1 ? normalized.slice(separatorIdx + 2) : "";
  const looksTruncated =
    normalized.length > 0 &&
    !normalized.endsWith("\n") &&
    separatorIdx !== -1 &&
    bodyTail.length > 16 &&
    /--[A-Za-z0-9_-]+\s*$/.test(bodyTail);
  if (looksTruncated) {
    defects.push({
      code: DEFECT.RAW_TRUNCATED_INPUT,
      source: "parser",
      severity: "warning",
      message: "Source ends mid-message without a trailing line terminator.",
      evidence: { layer: "raw" },
      recovery: "Parsed the bytes available; later content may be missing.",
    });
  }

  // PROVENANCE_INPUT_TRUNCATED. The CRLF-termination contract (RFC 5322 §2.1)
  // requires every line in a message to end with CRLF including the last.
  // When the source's final non-empty line does not, an upstream caller
  // likely cut the bytes mid-write (upload pipeline truncation, partial
  // fread, …). This is distinct from RAW_TRUNCATED_INPUT — that one requires
  // the truncation to look like a mid-MIME-boundary cut.
  // PROVENANCE_INPUT_TRUNCATED is the universal "missing CRLF on final
  // non-empty line" signal so even a single-part message surfaces the
  // warning.
  const finalNewlineMatch = /([^\r\n])\r?\n*$/.exec(source);
  const lastNonEmptyEnd = finalNewlineMatch
    ? source.lastIndexOf(finalNewlineMatch[1]!) + 1
    : -1;
  const endsWithCrlf = source.endsWith("\r\n");
  if (!endsWithCrlf && finalNewlineMatch && lastNonEmptyEnd === source.length) {
    defects.push({
      code: DEFECT.PROVENANCE_INPUT_TRUNCATED,
      source: "parser",
      severity: "warning",
      message:
        "Source's final non-empty line is not terminated by CRLF (RFC 5322 §2.1 violation; possible upstream truncation).",
      evidence: { layer: "raw" },
      recovery: "Parsed the bytes available; treat the result as provisional.",
    });
  }

  // I18N-001. RFC 6532 §3.1 says NFC SHOULD be used for non-ASCII header
  // values in internationalised email. We don't re-normalise the source
  // (round-trip preservation matters for SMTP- facing flows), but we surface
  // an info defect when the input is detectably non-NFC so consumers know the
  // displayed glyphs differ from the on-the-wire bytes. The check is cheap (a
  // single `normalize` round-trip on a short-circuit sample) and only fires
  // when the message actually carries non-ASCII bytes.
  if (/[\u0080-\uFFFF]/.test(normalized)) {
    try {
      const nfc = normalized.normalize("NFC");
      if (nfc !== normalized) {
        defects.push({
          code: DEFECT.HEADER_VALUE_NON_ASCII,
          source: "parser",
          severity: "info",
          message:
            "Source contains non-ASCII bytes that are not in NFC form (RFC 6532 §3.1 SHOULD).",
          evidence: { layer: "raw" },
          recovery: "Bytes kept verbatim; consumers may normalise at render time.",
        });
      }
    } catch {
      // Some runtimes may throw on exotic surrogate pairs; ignore.
    }
  }

  // Depth + count tracking shared across the recursive parsePart calls.
  // Allocated once so a single deeply-nested message emits exactly one
  // MIME_DEPTH_EXCEEDED rather than one per part.
  const limits: LimitState = {
    config,
    partCount: 0,
    partsExhausted: false,
    partsSkipped: 0,
    embeddedDepth: 0,
    archiveDepth: 0,
    didEmit: {
      mimeDepth: false,
      mimePartCount: false,
      embeddedDepth: false,
      archiveDepth: false,
      decodedPartBytes: false,
      attachmentBytes: false,
      headerBytes: false,
      headerCount: false,
      mimeNestedTooDeep: false,
    },
  };

  // Catalog H stage `headers-parse` + `mime-scan` — the recursive
  // parsePart walk does both: header unfolding plus multipart boundary
  // splitting. We split the timing into two phases by measuring the
  // top-level header parse separately from the recursive body walk.
  // For one-part messages they collapse into the same call so the
  // mime-scan stage records ~0; this is intentional — the budget table
  // accounts for the cheap-mime path.
  const headersStart = performance.now();
  onStage?.("parsingHeaders");
  // Thread the part's absolute byte offset inside the normalized source so
  // every recursively parsed EmailPart carries `partHeaderByteRange` +
  // `partBodyByteRange`. The root part starts at offset 0; the `partId` `"1"`
  // matches the dotted-path convention used by `walkParts` in the indicators
  // derivation + the MimeTree UI.
  const root = parsePart(normalized, defects, limits, 0, "1", 0);
  const parsePartMs = performance.now() - headersStart;
  // Split the parsePart cost: estimate headers vs mime via the
  // top-level body separator position. Header bytes / total bytes is
  // a reasonable proxy for the headers-parse portion; the remainder
  // is the multipart scan + nested header parses. Both stages get a
  // share that sums to the total parse-part cost so the rollup is
  // additive.
  const sourceLen = normalized.length || 1;
  const sepOffset = normalized.indexOf("\n\n");
  const headerRatio = sepOffset >= 0 ? Math.min(1, sepOffset / sourceLen) : 0.5;
  perf.record("headers-parse", parsePartMs * headerRatio);
  t("parsingHeaders", headersStart);

  const mimeStart = performance.now();
  onStage?.("buildingMime");
  const html = findBodyPart(root, "text/html");
  const text = findBodyPart(root, "text/plain");
  const mimeScanMs = performance.now() - mimeStart;
  perf.record("mime-scan", parsePartMs * (1 - headerRatio) + mimeScanMs);
  t("buildingMime", mimeStart);

  // Catalog H stages `body-decode` + `charset-normalise` — the bulk of
  // the byte work happens inside `parsePart` already (decoding base64 /
  // quoted-printable + charset conversion); we time the *attachment-
  // extraction* walk here, which traverses every leaf and copies bytes
  // out into the attachments list. The cost tracks total decoded-part
  // bytes so the budget reflects per-fixture-size byte volume.
  const decodeStart = performance.now();
  onStage?.("decoding");
  let decodedByteVolume = 0;
  walk(root, (p) => {
    decodedByteVolume += p.bytes.byteLength;
  });
  const decodeMs = performance.now() - decodeStart;
  perf.record("body-decode", decodeMs);
  // Touch decodedByteVolume so the optimiser keeps the walk.
  void decodedByteVolume;

  const charsetStart = performance.now();
  // Charset normalisation already ran inside parsePart's
  // `decodeBytes(bytes, charset, defects)` call. We measure a second-
  // pass scan that asks each text part for its `charset` field — this
  // mirrors what `analyzeEmail.charsetsSeen` does and keeps the budget
  // table covering the surface a downstream consumer would pay.
  const seenCharsets = new Set<string>();
  walk(root, (p) => {
    if (p.charset && p.contentType.startsWith("text/")) {
      seenCharsets.add(p.charset.toLowerCase());
    }
  });
  void seenCharsets;
  perf.record("charset-normalise", performance.now() - charsetStart);
  t("decoding", decodeStart);

  const extractStart = performance.now();
  onStage?.("extracting");
  const attachments: Attachment[] = [];
  const inlineParts: Record<string, EmailPart> = {};
  walk(root, (part) => {
    if (part.contentId && part.parts.length === 0) {
      inlineParts[part.contentId] = part;
    }
    if (!isAttachmentPart(part, html, text)) return;
    attachments.push({
      filename: part.filename ?? defaultFilename(part),
      contentType: part.contentType,
      size: part.bytes.byteLength,
      bytes: part.bytes,
      disposition: part.disposition === "attachment" ? "attachment" : "inline",
      contentId: part.contentId,
      // Thread the part's citation shape through so the AttachmentsList chip
      // can cite the attachment's byteRange / partId instead of `[0,
      // a.size]`.
      partId: part.partId,
      partHeaderByteRange: part.partHeaderByteRange,
      partBodyByteRange: part.partBodyByteRange,
    });
  });
  t("extracting", extractStart);

  const named = extractNamedHeaders(root.headers, root.headerList, defects);
  // Build the layered HeaderLayer view alongside the legacy `ParsedHeaders`
  // shape. They share name fields (`from`, `to`, `cc`, `subject`, `date`,
  // `messageId`) so the merged object exposes both legacy paths
  // (`headers.all`, `headers.raw`) and layered ones (`headers.ordered`,
  // `headers.byName`).
  const headersLayered: ParsedHeadersWithLayer = {
    ...named,
    schemaVersion: HEADERS_LAYER_VERSION,
    ordered: named.all,
    byName: named.raw,
  };

  // Populate the per-layer buckets. Most layers are thin re-exposures of
  // values already computed above (no recomputation).
  const get = (name: string) => named.raw[name.toLowerCase()];
  // `normalized` (like `source`) is a byte-per-code-unit string: each JS char
  // holds one original message byte (0x00-0xFF). The true byte length is
  // therefore the code-unit length itself. Do NOT use
  // `new TextEncoder().encode(normalized).length` here — TextEncoder re-encodes
  // every 0x80-0xFF char into a 2-byte UTF-8 sequence, inflating the count for
  // any non-ASCII content. (`Uint8Array.from(normalized, c => c.charCodeAt(0) &
  // 0xff).length` would give the same value as `normalized.length`.)
  const sourceByteLength = normalized.length;
  const bodySeparatorOffset = normalized.indexOf("\n\n");
  const artifactLayer: ArtifactLayer = {
    schemaVersion: ARTIFACT_LAYER_VERSION,
    sourceBytes: sourceByteLength,
  };
  const rawLayer: RawLayer = {
    schemaVersion: RAW_LAYER_VERSION,
    source: normalized,
    byteLength: sourceByteLength,
  };
  const syntaxLayer: SyntaxLayer = {
    schemaVersion: SYNTAX_LAYER_VERSION,
    hasCrlfLineEndings: /\r\n/.test(source),
    bodySeparatorOffset,
    headerLineCount: root.headerList.length,
  };
  const addressesLayer: AddressLayer = {
    schemaVersion: ADDRESSES_LAYER_VERSION,
    from: get("from"),
    to: get("to"),
    cc: get("cc"),
    bcc: get("bcc"),
    replyTo: get("reply-to"),
    sender: get("sender"),
    returnPath: get("return-path"),
  };
  const identityLayer: IdentityLayer = {
    schemaVersion: IDENTITY_LAYER_VERSION,
    subject: named.subject,
    rawDate: named.date,
    messageId: named.messageId,
  };
  // Received headers in source order. The legacy `headers.raw` map joined
  // duplicates with `\n`; split them back out for the envelope layer's array
  // view.
  const rawReceived = get("received");
  const receivedList = rawReceived ? rawReceived.split("\n") : [];
  const envelopeLayer: EnvelopeLayer = {
    schemaVersion: ENVELOPE_LAYER_VERSION,
    returnPath: get("return-path"),
    deliveredTo: get("delivered-to"),
    received: receivedList,
  };
  const threadingLayer: ThreadLayer = {
    schemaVersion: THREADING_LAYER_VERSION,
    messageId: get("message-id"),
    inReplyTo: get("in-reply-to"),
    references: get("references"),
    threadTopic: get("thread-topic"),
    threadIndex: get("thread-index"),
  };
  const timeLayer: TimeLayer = {
    schemaVersion: TIME_LAYER_VERSION,
    rawDate: named.date,
    rawResentDate: get("resent-date"),
    rawDeliveryDate: get("delivery-date"),
  };
  // Pre-walk the tree once so `mime.partCount` / `mime.depth` are O(1)
  // for downstream consumers.
  const treeStats = computeTreeStats(root);
  const mimeLayer: MimeLayer = {
    schemaVersion: MIME_LAYER_VERSION,
    tree: root,
    inlineParts,
    partCount: treeStats.count,
    depth: treeStats.depth,
  };
  // Apply RFC 2646 / RFC 3676 `format=flowed` unfolding when the chosen
  // text/plain part declares it. The raw `text.body` field stays
  // wire-faithful so provenance is preserved; only the convenience `text`
  // projection here uses the unfolded form so search-in-body and the preview
  // pane see clean paragraphs.
  const textBody = text ? decodeFlowedBody(text) : null;
  const bodiesLayer: BodyLayer = {
    schemaVersion: BODIES_LAYER_VERSION,
    html: html ? html.body : null,
    text: textBody,
    hasHtml: Boolean(html),
    hasText: Boolean(text),
  };
  const inlineCount = attachments.reduce(
    (n, a) => (a.disposition === "inline" ? n + 1 : n),
    0,
  );
  const attachmentsLayer: AttachmentLayer = {
    schemaVersion: ATTACHMENTS_LAYER_VERSION,
    list: attachments,
    totalBytes: attachments.reduce((n, a) => n + a.size, 0),
    inlineCount,
    fileCount: attachments.length - inlineCount,
  };
  // `embeddedMessages` walks the tree once for every `message/rfc822` child,
  // recording a path so deep-link evidence (chip → "open the embedded
  // message") can address it.
  const embeddedList: EmbeddedMessage[] = [];
  collectEmbeddedMessages(root, [], embeddedList);
  const embeddedLayer: EmbeddedMessageLayer = {
    schemaVersion: EMBEDDED_MESSAGES_LAYER_VERSION,
    list: embeddedList,
  };

  // Catalog H stage `defects-collect` — finalise the per-parse defects
  // collector. Cheap (just a `.list()` snapshot) but recorded so the
  // budget table has the full twelve-stage coverage on the parse side.
  const defectsStart = performance.now();
  const collectedDefects = defects.list();
  perf.record("defects-collect", performance.now() - defectsStart);

  const perfResult = perf.finalise();
  recordPerfSample("parse", perfResult);

  // Apply the Settings → Limits "Behaviour" toggles to the assembled
  // ParsedEmail. The parser-core walk above is unchanged so the citation
  // byte-ranges + defect emissions stay identical regardless of toggle state;
  // we project the toggles by hiding / blanking the affected slots here so
  // consumers downstream see exactly what the toggle promises (`Parse HTML =
  // off` → no html body surfaces; `Extract attachments = off` → metadata-only
  // list; `Preserve raw = off` → raw source dropped after derivations; etc.).
  // Defaults preserve existing behaviour — every toggle defaults to ON so a
  // user who never touched Settings sees no change.
  const effectiveHtml = config.parseHtml ? (html ? html.body : null) : null;
  const effectiveText = config.decodeBodies ? textBody : null;
  // `decodeBodies = false` keeps bodies in raw transfer-encoding: blank
  // the `body` projection on every text leaf (the `rawBody` field is
  // already kept verbatim, and `bytes` stays untouched so callers that
  // explicitly need the encoded bytes can still reach for them).
  if (!config.decodeBodies) {
    walk(root, (p) => {
      p.body = "";
    });
  }
  if (!config.parseHtml) {
    // Blank the html-leaf's body projection so a `parseHtml = off`
    // session can't accidentally render the HTML via `root` either.
    walk(root, (p) => {
      if (p.contentType === "text/html") p.body = "";
    });
  }
  // `preserveRaw = false` drops the raw source bytes once derivations
  // have run. The recursive parts keep their `rawBody` field cleared
  // alongside the top-level `raw.source` so memory footprint matches
  // the user's expectation ("don't keep the original around").
  if (!config.preserveRaw) {
    walk(root, (p) => {
      p.rawBody = "";
    });
  }
  const effectiveAttachments: Attachment[] = config.extractAttachments
    ? attachments
    : // List attachments without bytes so the user still sees what was
      // in the message but no copy of the payload is kept anywhere.
      attachments.map((a) => ({ ...a, bytes: new Uint8Array(0), size: 0 }));
  const effectiveAttachmentsLayer: AttachmentLayer = config.extractAttachments
    ? attachmentsLayer
    : {
        ...attachmentsLayer,
        list: effectiveAttachments,
        totalBytes: 0,
      };
  const effectiveBodiesLayer: BodyLayer = {
    ...bodiesLayer,
    html: effectiveHtml,
    text: effectiveText,
    hasHtml: bodiesLayer.hasHtml && config.parseHtml,
    hasText: bodiesLayer.hasText && config.decodeBodies,
  };
  const effectiveRawLayer: RawLayer = config.preserveRaw
    ? rawLayer
    : { ...rawLayer, source: "" };

  return {
    // Legacy flat fields — compat shim. Migrating each consumer to the
    // layered paths is a long-tail follow-up.
    headers: headersLayered,
    root,
    html: effectiveHtml,
    text: effectiveText,
    attachments: effectiveAttachments,
    inlineParts,
    defects: collectedDefects,
    // Catalog Q1 layered shape.
    schemaVersion: PARSED_EMAIL_SCHEMA_VERSION,
    artifact: artifactLayer,
    raw: effectiveRawLayer,
    syntax: syntaxLayer,
    addresses: addressesLayer,
    identity: identityLayer,
    envelope: envelopeLayer,
    threading: threadingLayer,
    time: timeLayer,
    mime: mimeLayer,
    bodies: effectiveBodiesLayer,
    attachmentsLayer: effectiveAttachmentsLayer,
    embeddedMessages: embeddedLayer,
    perf: perfResult,
  };
}

/**
 * Walk the MIME tree collecting every `message/rfc822` leaf into a flat list
 * with its tree-path. Used by the `embeddedMessages` layer so consumers don't
 * have to re-walk the tree.
 *
 * MIME-007 / I18N-002. RFC 6532 §3.7 defines `message/global` as "identical
 * to message/rfc822, except that 'globally-addressable' parts allow UTF-8 in
 * header values". The embedded-message surface treats both interchangeably so
 * a tour of an SMTPUTF8 message exposes the same inspect-the-inner-message
 * UX.
 */
export function isEmbeddedMessageType(contentType: string): boolean {
  return (
    contentType === "message/rfc822" ||
    contentType === "message/global" ||
    contentType === "message/global-headers" ||
    contentType === "message/rfc822-headers"
  );
}

function collectEmbeddedMessages(
  part: EmailPart,
  path: number[],
  out: EmbeddedMessage[],
): void {
  if (isEmbeddedMessageType(part.contentType)) {
    out.push({ path: [...path], part });
  }
  for (let i = 0; i < part.parts.length; i++) {
    collectEmbeddedMessages(part.parts[i]!, [...path, i], out);
  }
}

/**
 * Pre-walk summary used by the `mime` layer. Counts every part in the tree
 * (root + descendants) and the maximum nesting depth. A single recursive pass
 * keeps both numbers consistent and lets the layer expose them as O(1) reads.
 */
function computeTreeStats(part: EmailPart): { count: number; depth: number } {
  let count = 1;
  let depth = 0;
  for (const child of part.parts) {
    const sub = computeTreeStats(child);
    count += sub.count;
    if (sub.depth + 1 > depth) depth = sub.depth + 1;
  }
  return { count, depth };
}

// ---------- CID resolution ----------

/**
 * Rewrite `cid:XXX` URIs in an HTML string with inline `data:` URLs backed by
 * the actual bytes of the referenced MIME part. Handles: - HTML attributes:
 * src, href, background, poster (quoted or unquoted). - CSS `url(cid:...)`
 * inside `<style>` blocks and `style=""` attributes. - URL-encoded
 * Content-IDs (RFC 2392).
 */
export function resolveCidReferences(
  html: string,
  inlineParts: Record<string, EmailPart>,
): string {
  if (!html || Object.keys(inlineParts).length === 0) return html;

  const lookup = (raw: string): string | null => {
    const cid = decodeCidValue(raw);
    const part = inlineParts[cid];
    if (!part) return null;
    const type = part.contentType || "application/octet-stream";
    return `data:${type};base64,${bytesToBase64(part.bytes)}`;
  };

  let out = html.replace(
    /(src|href|background|poster)\s*=\s*(?:"cid:([^"]+)"|'cid:([^']+)'|cid:([^\s>]+))/gi,
    (match, attr: string, dq?: string, sq?: string, nq?: string) => {
      const cidValue = dq ?? sq ?? nq ?? "";
      const url = lookup(cidValue);
      return url ? `${attr}="${url}"` : match;
    },
  );

  out = out.replace(
    /url\(\s*(['"]?)cid:([^)'"]+)\1\s*\)/gi,
    (match, _quote: string, cidValue: string) => {
      const url = lookup(cidValue);
      return url ? `url("${url}")` : match;
    },
  );

  return out;
}

function decodeCidValue(cid: string): string {
  try {
    return decodeURIComponent(cid);
  } catch {
    // Malformed percent-encoding (e.g. raw `%` followed by non-hex). Per
    // the parser's permissive contract: return the raw value rather than
    // throw — the caller will still attempt a lookup with the original.
    return cid;
  }
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    const slice = bytes.subarray(i, Math.min(i + chunk, bytes.length));
    binary += bytesToBinaryString(slice);
  }
  return btoa(binary);
}

// ---------- Recursive MIME parsing ----------

/**
 * Shared tracking state for the recursive parsePart traversal. Allocated once
 * per `parseEmail` call so depth / count limits trip once per parse rather
 * than once per node.
 */
interface LimitState {
  config: ParserConfig;
  partCount: number;
  /**
   * Set once `partCount` passes `maxMimePartCount`. The traversal used to
   * emit a defect and keep walking, so the cap bounded nothing: a message
   * with a million parts still built a million nodes. Once this is set no
   * further container is descended into.
   */
  partsExhausted: boolean;
  /** Sub-parts never parsed because the cap was already reached. */
  partsSkipped: number;
  embeddedDepth: number;
  archiveDepth: number;
  didEmit: {
    mimeDepth: boolean;
    mimePartCount: boolean;
    embeddedDepth: boolean;
    archiveDepth: boolean;
    decodedPartBytes: boolean;
    attachmentBytes: boolean;
    headerBytes: boolean;
    headerCount: boolean;
    mimeNestedTooDeep: boolean;
  };
}

function parsePart(
  raw: string,
  defects: DefectCollector | undefined,
  limits?: LimitState,
  depth = 0,
  /**
   * Dotted-path id of this part (`"1"` for the root, `"1.2"` for the second
   * child of the root, …). Carried so the resulting `EmailPart` can stamp its
   * own `partId` for chip / indicator citations.
   */
  partId: string = "1",
  /**
   * Absolute byte offset of the part's raw text inside the normalized source.
   * The root passes 0; the recursive multipart splitter passes the byte
   * offset of each child slice. Used to compute `partHeaderByteRange` +
   * `partBodyByteRange` without re-scanning the source.
   */
  partAbsoluteOffset: number = 0,
): EmailPart {
  const { headers, body, bodyOffset } = splitHeadersAndBody(raw);
  const { map: headerMap, list: headerList } = parseHeaders(headers, defects, depth);

  // MIME_PART_HEADER_MALFORMED. A MIME part whose header block has no
  // parseable `Name: value` line at all (depth > 0 and at least one non-blank
  // line in the raw input but zero entries in the unfolded header list) is
  // structurally unrecoverable. The part still surfaces as
  // application/octet-stream so the consumer sees the bytes.
  if (depth > 0 && headerList.length === 0 && headers.replace(/\s+/g, "").length > 0) {
    defects?.push({
      code: DEFECT.MIME_PART_HEADER_MALFORMED,
      source: "parser",
      severity: "warning",
      message: "MIME part header block had no parseable `Name: value` fields.",
      evidence: { layer: "mime", fieldPath: "part.headers" },
      recovery: "Treated the part as application/octet-stream; raw bytes preserved.",
    });
  }

  // Header bytes / count limits. Top-level only (depth 0) so per-part headers
  // don't double-count against the message- wide budget.
  if (limits && depth === 0) {
    if (headers.length > limits.config.maxHeaderBytes && !limits.didEmit.headerBytes) {
      limits.didEmit.headerBytes = true;
      defects?.push({
        code: DEFECT.HEADER_BYTES_EXCEEDED,
        source: "parser",
        severity: "warning",
        message: `Header block bytes (${headers.length}) exceed maxHeaderBytes (${limits.config.maxHeaderBytes}).`,
        evidence: { layer: "headers" },
        recovery: "Continued parsing the headers within the limit window.",
      });
    }
    if (headerList.length > limits.config.maxHeaderCount && !limits.didEmit.headerCount) {
      limits.didEmit.headerCount = true;
      defects?.push({
        code: DEFECT.HEADER_COUNT_EXCEEDED,
        source: "parser",
        severity: "warning",
        message: `Header count (${headerList.length}) exceeds maxHeaderCount (${limits.config.maxHeaderCount}).`,
        evidence: { layer: "headers" },
        recovery:
          "All header fields kept; downstream consumers may apply their own caps.",
      });
    }
  }

  // MIME-004. RFC 2045 §5.1 says "If an entity is of type 'multipart' the
  // Content-Type field MUST appear exactly once" — and more generally an
  // entity has exactly one Content-Type. `parseHeaders` joins duplicates with
  // `\n` into the lookup map; we surface the spec break and keep the first
  // occurrence so the downstream Content-Type parser sees a clean value
  // rather than `text/html\ntext/plain`.
  let contentTypeHeader = headerMap["content-type"] ?? "text/plain";
  if (contentTypeHeader.includes("\n")) {
    defects?.push({
      code: DEFECT.MIME_PART_HEADER_MALFORMED,
      source: "parser",
      severity: "warning",
      message:
        "Multiple Content-Type headers on one part; RFC 2045 §5.1 allows exactly one.",
      evidence: { layer: "mime", fieldPath: "headers.content-type" },
      recovery: "Kept the first occurrence; later Content-Type headers ignored.",
    });
    contentTypeHeader = contentTypeHeader.split("\n", 1)[0]!;
  }
  const { type, params } = parseContentType(contentTypeHeader, defects);
  const charset = (params.charset ?? "utf-8").toLowerCase();
  const transferEncoding = (headerMap["content-transfer-encoding"] ?? "7bit")
    .trim()
    .toLowerCase();

  const disposition = parseDisposition(headerMap["content-disposition"], defects);
  const filename = decodeEncodedWordWithDefects(
    disposition?.filename ?? params.name ?? "",
    defects,
    "filename",
  );

  // Count this node toward the part-count budget and emit a single defect
  // when the cap is first crossed.
  if (limits) {
    limits.partCount += 1;
    if (
      limits.partCount > limits.config.maxMimePartCount &&
      !limits.didEmit.mimePartCount
    ) {
      limits.didEmit.mimePartCount = true;
      defects?.push({
        code: DEFECT.MIME_PART_COUNT_EXCEEDED,
        source: "parser",
        severity: "warning",
        message: `MIME part count exceeded maxMimePartCount (${limits.config.maxMimePartCount}).`,
        evidence: { layer: "mime" },
        recovery: "Stopped descending into further containers at the cap.",
      });
    }
    // Set every time, not only on the first trip: the defect is once-per-parse
    // but the stop has to hold for the rest of the traversal.
    if (limits.partCount > limits.config.maxMimePartCount) limits.partsExhausted = true;
  }

  // Citation byte ranges. The `partHeaderByteRange` covers the part's header
  // block (offsets [partAbsoluteOffset, partAbsoluteOffset + bodyOffset)
  // where bodyOffset is the start-of-body offset inside the local `raw`
  // slice, which includes the blank-line separator the unfolder consumed).
  // The `partBodyByteRange` covers the encoded body bytes inside the source
  // (so a permalink consumer rendering the raw.eml can scroll to the bytes
  // the chip cites).
  const partHeaderByteRange: [number, number] = [
    partAbsoluteOffset,
    partAbsoluteOffset + bodyOffset,
  ];
  const partBodyByteRange: [number, number] = [
    partAbsoluteOffset + bodyOffset,
    partAbsoluteOffset + bodyOffset + body.length,
  ];
  const part: EmailPart = {
    headers: headerMap,
    headerList,
    contentType: type,
    charset,
    transferEncoding,
    disposition: disposition?.type,
    filename: filename || undefined,
    contentId: stripAngleBrackets(headerMap["content-id"]),
    format: params.format?.toLowerCase(),
    delsp: params.delsp?.toLowerCase(),
    body: "",
    bytes: new Uint8Array(0),
    rawBody: body,
    parts: [],
    partId,
    partHeaderByteRange,
    partBodyByteRange,
  };

  // MIME-001 + MIME-002. RFC 2046 §5.1.1 makes the boundary parameter
  // mandatory on every `multipart/*` Content-Type and constrains it to 1-70
  // chars from a fixed `bchars` alphabet (`DIGIT / ALPHA / "'" / "(" / ")" /
  // "+" / "_" / "," / "-" / "." / "/" / ":" / "=" / "?" / SPACE`), with the
  // trailing char restricted to `bcharsnospace`. Probe p16 showed
  // `boundary=""` silently produced a one-part tree with no defect (the body
  // was consumed). Empty / missing boundary now emits MIME_BOUNDARY_MISSING;
  // out-of-spec alphabet / length emits MIME_PART_HEADER_MALFORMED so the
  // user can tell why a malformed message refused to split.
  if (type.startsWith("multipart/")) {
    const boundaryParam = params.boundary;
    if (!boundaryParam || boundaryParam.length === 0) {
      defects?.push({
        code: DEFECT.MIME_BOUNDARY_MISSING,
        source: "parser",
        severity: "warning",
        message: `multipart/* part has ${boundaryParam === undefined ? "no" : "an empty"} boundary parameter; RFC 2046 §5.1.1 requires 1-70 chars.`,
        evidence: { layer: "mime", fieldPath: "headers.content-type" },
        recovery: "Container kept as a leaf part; sub-parts not extracted.",
      });
    } else {
      // RFC 2046 §5.1.1 bchars alphabet. We allow the SPACE in the
      // interior but not at the end (bcharsnospace rule). The check is
      // emit-only — the splitter still uses whatever the sender sent.
      const bcharsRe = /^[0-9A-Za-z'()+_,\-./:=? ]+$/;
      const endsInSpace = /[ \t]$/.test(boundaryParam);
      if (boundaryParam.length > 70 || !bcharsRe.test(boundaryParam) || endsInSpace) {
        defects?.push({
          code: DEFECT.MIME_PART_HEADER_MALFORMED,
          source: "parser",
          severity: "info",
          message:
            boundaryParam.length > 70
              ? `Boundary parameter is ${boundaryParam.length} chars; RFC 2046 §5.1.1 caps at 70.`
              : endsInSpace
                ? "Boundary parameter ends with whitespace; RFC 2046 §5.1.1 forbids trailing LWSP."
                : `Boundary parameter contains characters outside the RFC 2046 §5.1.1 bchars alphabet.`,
          evidence: { layer: "mime", fieldPath: "headers.content-type" },
          recovery:
            "Container split using the boundary as-is; alphabet mismatch ignored.",
        });
      }
    }
  }

  if (type.startsWith("multipart/") && params.boundary) {
    if (limits && depth + 1 > limits.config.maxMimeDepth && !limits.didEmit.mimeDepth) {
      limits.didEmit.mimeDepth = true;
      defects?.push({
        code: DEFECT.MIME_DEPTH_EXCEEDED,
        source: "parser",
        severity: "warning",
        message: `MIME depth exceeded maxMimeDepth (${limits.config.maxMimeDepth}).`,
        evidence: { layer: "mime" },
        recovery: "Returning a flat part at the cap; deeper sub-parts not walked.",
      });
      // Do not recurse further — produce a flat part the caller can
      // still display.
      return part;
    }
    // MIME_NESTED_TOO_DEEP. Distinct from MIME_DEPTH_EXCEEDED (the hard H12
    // limit-trip). This fires when nesting crosses a soft threshold of
    // `maxMimeDepth / 2` — the tree is still walked, but the depth itself is
    // unusual enough to surface in the Defects tab. Once-per-parse, so a
    // deeply-nested message doesn't spam the collector.
    const softDepthThreshold = Math.max(2, Math.floor(limits!.config.maxMimeDepth / 2));
    if (
      limits &&
      depth + 1 > softDepthThreshold &&
      depth + 1 <= limits.config.maxMimeDepth &&
      !limits.didEmit.mimeNestedTooDeep
    ) {
      limits.didEmit.mimeNestedTooDeep = true;
      defects?.push({
        code: DEFECT.MIME_NESTED_TOO_DEEP,
        source: "parser",
        severity: "info",
        message: `MIME nesting reached depth ${depth + 1} (soft threshold ${softDepthThreshold}).`,
        evidence: { layer: "mime" },
        recovery: "Continued walking the tree; consumers may flag deep nesting.",
      });
    }
    // BodyOffset for a multipart container points at the start of the body
    // (after the header separator). Each child's absolute offset = container
    // offset + bodyOffset (where the body begins inside `raw`) + child offset
    // inside the body.
    // The splitter is given the remaining budget, so it stops SLICING at the
    // point the loop below would stop parsing. It keeps counting, so `total`
    // is still the real number of children and the reader is still told how
    // many were skipped.
    // `+ 1` because the cap is enforced AFTER a part is counted: the child
    // that tips `partCount` past the limit is still parsed, and was before
    // this. Slicing one more than the budget keeps that boundary behaviour
    // byte-identical instead of quietly dropping the last part.
    const remaining = limits
      ? Math.max(0, limits.config.maxMimePartCount - limits.partCount) + 1
      : Number.POSITIVE_INFINITY;
    const { children: subParts, total: subTotal } = splitMultipart(
      body,
      params.boundary,
      defects,
      remaining,
    );
    // Per CHILD, not once per container. The hostile shape is a single
    // multipart with a million leaves, so a guard that only refused to descend
    // into the next container would never fire on it: there is no next
    // container. Each child is checked before it is parsed, because parsing it
    // is what allocates a node and decodes a body.
    const kept: EmailPart[] = [];
    for (let i = 0; i < subTotal; i++) {
      if (limits?.partsExhausted || i >= subParts.length) {
        if (limits) limits.partsSkipped += subTotal - i;
        part.partsTruncated = true;
        break;
      }
      const sub = subParts[i]!;
      kept.push(
        parsePart(
          sub.text,
          defects,
          limits,
          depth + 1,
          `${partId}.${i + 1}`,
          partAbsoluteOffset + bodyOffset + sub.bodyOffset,
        ),
      );
    }
    part.parts = kept;
    return part;
  }

  // Embedded `message/rfc822` / `message/global` (RFC 6532 §3.7) increments
  // embedded depth.
  if (isEmbeddedMessageType(type) && limits) {
    limits.embeddedDepth += 1;
    if (
      limits.embeddedDepth > limits.config.maxEmbeddedDepth &&
      !limits.didEmit.embeddedDepth
    ) {
      limits.didEmit.embeddedDepth = true;
      defects?.push({
        code: DEFECT.EMBEDDED_DEPTH_EXCEEDED,
        source: "parser",
        severity: "warning",
        message: `Embedded message depth exceeded maxEmbeddedDepth (${limits.config.maxEmbeddedDepth}).`,
        evidence: { layer: "mime" },
        recovery: "Stopped walking embedded messages past the cap.",
      });
    }
  }

  const bytes = decodeTransferEncoding(body, transferEncoding, defects);
  part.bytes = bytes;
  if (limits) {
    // The tightest cap that applies to this part. An attachment is bounded by
    // both, so take the smaller: whichever fires, the retained buffer is cut
    // to it. Previously both limits only emitted a defect saying "bytes
    // retained", which is the opposite of containment: the whole decoded
    // payload stayed on the node for the rest of the session.
    const isAttachment = disposition?.type === "attachment";
    const cap = isAttachment
      ? Math.min(limits.config.maxDecodedPartBytes, limits.config.maxAttachmentBytes)
      : limits.config.maxDecodedPartBytes;

    if (
      bytes.byteLength > limits.config.maxDecodedPartBytes &&
      !limits.didEmit.decodedPartBytes
    ) {
      limits.didEmit.decodedPartBytes = true;
      defects?.push({
        code: DEFECT.DECODED_PART_BYTES_EXCEEDED,
        source: "parser",
        severity: "warning",
        message: `Decoded part bytes (${bytes.byteLength}) exceed maxDecodedPartBytes (${limits.config.maxDecodedPartBytes}).`,
        evidence: { layer: "mime" },
        recovery: "Truncated the retained bytes to the limit; the part is a prefix.",
      });
    }
    if (
      isAttachment &&
      bytes.byteLength > limits.config.maxAttachmentBytes &&
      !limits.didEmit.attachmentBytes
    ) {
      limits.didEmit.attachmentBytes = true;
      defects?.push({
        code: DEFECT.ATTACHMENT_BYTES_EXCEEDED,
        source: "parser",
        severity: "warning",
        message: `Attachment bytes (${bytes.byteLength}) exceed maxAttachmentBytes (${limits.config.maxAttachmentBytes}).`,
        evidence: { layer: "attachments" },
        recovery: "Truncated the retained bytes to the limit; the part is a prefix.",
      });
    }
    if (bytes.byteLength > cap) {
      // `subarray` would keep the original ArrayBuffer alive, which is the
      // whole allocation this is meant to release. `slice` copies.
      part.bytes = bytes.slice(0, cap);
      part.bytesTruncated = true;
    }
  }
  if (type.startsWith("text/") || isEmbeddedMessageType(type)) {
    // `part.bytes`, not `bytes`: a truncated part must not decode its full
    // payload into a string and put the allocation straight back.
    part.body = decodeBytes(part.bytes, charset, defects);
  }
  return part;
}

function splitHeadersAndBody(raw: string): {
  headers: string;
  body: string;
  /**
   * Byte offset (inside `raw`) where the body starts. When no separator is
   * present and the entire input is treated as headers, `bodyOffset ===
   * raw.length`.
   */
  bodyOffset: number;
} {
  const separator = raw.indexOf("\n\n");
  if (separator === -1) {
    return { headers: raw, body: "", bodyOffset: raw.length };
  }
  return {
    headers: raw.slice(0, separator),
    body: raw.slice(separator + 2),
    bodyOffset: separator + 2,
  };
}

function parseHeaders(
  raw: string,
  defects?: DefectCollector,
  depth = 0,
): {
  map: Record<string, string>;
  list: HeaderEntry[];
} {
  // Unfold continuation lines (RFC 5322 § 2.2.3). Continuation lines begin
  // with WSP and are concatenated with a single space to the previous header.
  const unfolded: string[] = [];
  // HEADER_FOLDING_MALFORMED. RFC 5322 §2.2.3 requires every continuation
  // line to begin with WSP. We detect the inverse — a line that *looks* like
  // it continues a header (no `:`, no blank, not first) but lacks leading
  // whitespace. The legacy unfolder already swallows these as separate
  // "headers" with no colon; we emit the defect so the malformation is
  // visible.
  let foldingMalformedEmitted = false;
  const splitLines = raw.split("\n");
  for (let i = 0; i < splitLines.length; i++) {
    const line = splitLines[i]!;
    if (/^[ \t]/.test(line) && unfolded.length > 0) {
      // HDR-005. RFC 5322 §3.2.2 forbids a folded-header line that is
      // composed entirely of WSP ("any line of a folded header field [MUST
      // NOT be] made up entirely of WSP characters and nothing else"). The
      // unfolder still glues such a line for back-compat, but we surface the
      // defect so the anomaly is visible.
      if (depth === 0 && !foldingMalformedEmitted && /^[ \t]+$/.test(line)) {
        foldingMalformedEmitted = true;
        defects?.push({
          code: DEFECT.HEADER_FOLDING_MALFORMED,
          source: "parser",
          severity: "info",
          message:
            "Folded header continuation line is entirely WSP (RFC 5322 §3.2.2 forbids it).",
          evidence: { layer: "headers", lineRange: [i, i] },
          recovery: "Glued the WSP-only line onto the previous header.",
        });
      }
      unfolded[unfolded.length - 1] += " " + line.trim();
    } else {
      // HDR-003. RFC 5322 §2.2 says a header line starts with a field-name;
      // only a folded continuation may start with WSP. A leading-WSP first
      // line (or any non-blank WSP-prefixed line with no preceding header) is
      // malformed. The earlier `if` branch only handles WSP-prefixed lines
      // when a previous header exists; here we surface the orphan case.
      if (
        depth === 0 &&
        !foldingMalformedEmitted &&
        unfolded.length === 0 &&
        line.length > 0 &&
        /^[ \t]/.test(line)
      ) {
        foldingMalformedEmitted = true;
        defects?.push({
          code: DEFECT.HEADER_FOLDING_MALFORMED,
          source: "parser",
          severity: "info",
          message:
            "First header line starts with WSP; RFC 5322 §2.2 requires a field-name.",
          evidence: { layer: "headers", lineRange: [i, i] },
          recovery:
            "Treated the line as a header attempt; field-name may parse as empty.",
        });
      }
      // Heuristic for malformed folding: a non-blank line at i>0 with
      // no `:` separator, when the previous line has at least one
      // header (looked like `Name: value`). The line could be a
      // continuation that lost its leading WSP, or a junk prefix line.
      // RCV-002. We previously treated such a line as a *fresh* header
      // attempt and then dropped it (no colon). That truncated `Received:
      // from a\nby b; ts` to just `from a`, hiding half the trust chain. Now
      // we recover by gluing the orphan line onto the previous header
      // (best-effort per RFC 5322 §2.2.3 obs-FWS) and still emit
      // HEADER_FOLDING_MALFORMED so the malformation surfaces in the Defects
      // tab. RCV-002. A line with a `:` may still be an orphan continuation
      // rather than a fresh header (the timestamp segment of a Received
      // header carries colons, so `by host; Mon, 01 Jan 2024 00:00:00 +0000`
      // looks like a header named `by host; Mon, 01 Jan 2024 00`). We treat
      // such a line as a best-effort continuation when (a) it has no colon,
      // OR (b) the pre-colon prefix is not a valid field-name AND the
      // previous header is one of the multi-line-friendly trace / auth
      // fields. The narrow whitelist preserves HDR-001's `X Bad Name: value`
      // detection (single space in the name, previous header was an ordinary
      // single-line field).
      const colonIdx = line.indexOf(":");
      const prefixLooksLikeFieldName =
        colonIdx > 0 && /^[!-9;-~]+$/.test(line.slice(0, colonIdx).trim());
      const prevHeader = unfolded[unfolded.length - 1] ?? "";
      const prevColon = prevHeader.indexOf(":");
      const prevName =
        prevColon > 0 ? prevHeader.slice(0, prevColon).trim().toLowerCase() : "";
      const prevIsMultilineFriendly =
        prevName === "received" ||
        prevName === "authentication-results" ||
        prevName === "dkim-signature" ||
        prevName === "arc-seal" ||
        prevName === "arc-message-signature" ||
        prevName === "arc-authentication-results" ||
        prevName === "received-spf" ||
        prevName === "x-received";
      const isMalformedContinuation =
        i > 0 &&
        line.length > 0 &&
        unfolded.length > 0 &&
        prevColon !== -1 &&
        (colonIdx === -1 || (!prefixLooksLikeFieldName && prevIsMultilineFriendly));
      if (isMalformedContinuation) {
        if (!foldingMalformedEmitted && depth === 0) {
          foldingMalformedEmitted = true;
          defects?.push({
            code: DEFECT.HEADER_FOLDING_MALFORMED,
            source: "parser",
            severity: "info",
            message:
              "Header continuation line lacks the required leading WSP (RFC 5322 §2.2.3).",
            evidence: { layer: "headers", lineRange: [i, i] },
            recovery: "Glued the orphan line onto the previous header (best-effort).",
          });
        }
        unfolded[unfolded.length - 1] += " " + line.trim();
      } else {
        unfolded.push(line);
      }
    }
  }

  const map: Record<string, string> = {};
  const list: HeaderEntry[] = [];
  // HEADER_INVALID_NAME + HEADER_VALUE_NON_ASCII gates. RFC 5322 §3.6.8
  // limits field-name to printable ASCII minus `:` and whitespace; values
  // must be 7-bit ASCII unless an encoded-word wraps the non-ASCII bytes.
  // Per-name emit-once so a malformed message doesn't spam the collector.
  let invalidNameEmitted = false;
  let nonAsciiEmitted = false;
  let obsoleteFormEmitted = false;
  // HEADER_DUPLICATE_SINGLETON. RFC 5322 §3.6 table caps each of these fields
  // at max=1. When the same singleton appears more than once the parser keeps
  // the FIRST occurrence in `map` (so downstream lookups don't get a
  // `\n`-joined Frankenstein value that mis-tokenises into the address
  // parser) and emits a warning defect once per duplicated name.
  const singletonHeaders = new Set([
    "from",
    "sender",
    "reply-to",
    "to",
    "cc",
    "bcc",
    "message-id",
    "in-reply-to",
    "references",
    "subject",
    "date",
  ]);
  const duplicateSingletonEmitted = new Set<string>();
  // RFC 5322 ftext: printable ASCII (0x21-0x7E) minus colon (0x3A).
  const validFieldName = /^[!-9;-~]+$/;
  for (const line of unfolded) {
    const idx = line.indexOf(":");
    if (idx === -1) continue;
    const rawName = line.slice(0, idx);
    const name = rawName.trim();
    const value = line.slice(idx + 1).trim();
    if (!name) continue;

    // HDR-006. RFC 5322 §4.5 obs-optional permits `field-name *WSP ":"` but
    // receivers SHOULD flag the obsolete form. Detect by comparing the raw
    // pre-colon slice to its trimmed form: a `Subject:` line has trailing WSP
    // we don't otherwise see.
    if (depth === 0 && !obsoleteFormEmitted && /[ \t]$/.test(rawName)) {
      obsoleteFormEmitted = true;
      defects?.push({
        code: DEFECT.HEADER_FOLDING_MALFORMED,
        source: "parser",
        severity: "info",
        message: `Header "${name}" has WSP before the colon (RFC 5322 §4.5 obs-optional).`,
        evidence: { layer: "headers", fieldPath: `headers.${name.toLowerCase()}` },
        recovery: "Trimmed the trailing WSP from the field-name; value kept as-is.",
      });
    }

    if (depth === 0 && !invalidNameEmitted && !validFieldName.test(name)) {
      invalidNameEmitted = true;
      defects?.push({
        code: DEFECT.HEADER_INVALID_NAME,
        source: "parser",
        severity: "warning",
        message: `Header name "${name}" violates the RFC 5322 field-name grammar (non-token character).`,
        evidence: { layer: "headers", fieldPath: `headers.${name.toLowerCase()}` },
        recovery: "Header kept; downstream consumers may filter.",
      });
    }
    // RFC 5322 — raw header bytes outside ASCII (0x80+) without an
    // encoded-word wrapper. The value still decodes via TextDecoder
    // earlier; this defect surfaces the spec violation.
    if (
      depth === 0 &&
      !nonAsciiEmitted &&
      /[\u0080-\uFFFF]/.test(value) &&
      !value.includes("=?")
    ) {
      nonAsciiEmitted = true;
      defects?.push({
        code: DEFECT.HEADER_VALUE_NON_ASCII,
        source: "parser",
        severity: "info",
        message: `Header "${name}" contains raw 8-bit bytes outside an RFC 2047 encoded-word.`,
        evidence: { layer: "headers", fieldPath: `headers.${name.toLowerCase()}` },
        recovery: "Value kept verbatim; UTF-8 decode applied at display time.",
      });
    }
    list.push({ name, value });

    const lower = name.toLowerCase();
    if (lower in map) {
      if (singletonHeaders.has(lower)) {
        // HDR-004. RFC 5322 §3.6 caps these headers at max=1; don't join the
        // duplicate (the legacy `\n`-join caused `parseAddressList` to
        // mis-tokenise the second mailbox as a continuation of the first).
        // Keep the first occurrence in `map` per §3.6 ("first" is the safer
        // interpretation); both values remain visible in `headerList`.
        if (depth === 0 && !duplicateSingletonEmitted.has(lower)) {
          duplicateSingletonEmitted.add(lower);
          defects?.push({
            code: DEFECT.HEADER_DUPLICATE_SINGLETON,
            source: "parser",
            severity: "warning",
            message: `Header "${name}" appears more than once; RFC 5322 §3.6 allows at most one.`,
            evidence: { layer: "headers", fieldPath: `headers.${lower}` },
            recovery: "Kept the first occurrence; duplicates remain in headerList.",
          });
        }
      } else {
        // Preserve multi-valued headers (Received, Authentication-Results,
        // ARC-*, DKIM-Signature, X-Received, etc.) in the lookup map by
        // concatenating with a newline — callers that just want the first
        // occurrence can split on "\n".
        map[lower] = map[lower] + "\n" + value;
      }
    } else {
      map[lower] = value;
    }
  }
  return { map, list };
}

function parseContentType(
  header: string,
  defects?: DefectCollector,
  fieldPath = "headers.content-type",
): {
  type: string;
  params: Record<string, string>;
} {
  const [typePart = "", ...rest] = header.split(";");
  const rawParams: Record<string, string> = {};
  for (const chunk of rest) {
    const eq = chunk.indexOf("=");
    if (eq === -1) continue;
    const key = chunk.slice(0, eq).trim().toLowerCase();
    let value = chunk.slice(eq + 1).trim();
    if (value.startsWith('"') && value.endsWith('"') && value.length >= 2) {
      value = value.slice(1, -1);
      // MIME-005. RFC 2045 §5.1 follows RFC 822 quoted-string semantics: `\"`
      // inside the literal resolves to `"`, and `\\` resolves to `\`. The
      // legacy reader returned the wire form verbatim (`hello
      // \"world\".txt`); the unescape brings filenames / charset names back
      // to the intended display value.
      value = value.replace(/\\(.)/g, "$1");
    }
    rawParams[key] = value;
  }
  const params = joinRfc2231Continuations(rawParams, defects, fieldPath);
  return { type: typePart.trim().toLowerCase(), params };
}

function parseDisposition(
  header: string | undefined,
  defects?: DefectCollector,
): { type: string; filename?: string } | undefined {
  if (!header) return undefined;
  const { type, params } = parseContentType(
    header,
    defects,
    "headers.content-disposition",
  );
  return { type, filename: params.filename };
}

/**
 * Fold RFC 2231 parameter continuations and charset+language prefixes back
 * into one logical value per parameter name.
 *
 * Three shapes per RFC 2231:
 *
 * - `name="value"` — already a single key/value; pass through. -
 * `name*0="part1"; name*1="part2"; name*2="part3"` — concatenated in
 * numeric-index order to one string. - `name*0*=utf-8'en'%E2%82%AC;
 * name*1*=…` — first segment carries the `charset'lang'` prefix; every
 * starred segment is percent-decoded against that charset; non-starred
 * segments are appended verbatim. Language tag is preserved in the recovery
 * note but does not affect the decoded string.
 *
 * Malformed segments (bad percent-encoding, unknown charset that the
 * runtime's TextDecoder rejects) emit `HEADER_PARAM_DECODE_FAILED` severity
 * `info`; the value falls back to the raw on-the-wire form so downstream
 * consumers still see something.
 */
function joinRfc2231Continuations(
  raw: Record<string, string>,
  defects: DefectCollector | undefined,
  fieldPath: string,
): Record<string, string> {
  const out: Record<string, string> = {};
  // Group: bareName → { encoded:boolean, segments: [{idx, encoded, value}] }
  const groups = new Map<
    string,
    {
      // Some segments may be encoded (`name*N*=`), some not (`name*N=`).
      // Track per-segment so the join honours each shape.
      segments: Array<{ idx: number; encoded: boolean; value: string }>;
      // First-segment charset+language ends up here when present.
      charset?: string;
      language?: string;
    }
  >();

  for (const [key, value] of Object.entries(raw)) {
    // Match: name (no continuation), name* (single encoded), name*N,
    // name*N* (segmented + encoded). Order of the alternatives matters
    // because the regexes are anchored.
    const sectioned = /^([A-Za-z0-9_-]+)\*(\d+)(\*?)$/.exec(key);
    const wholeEncoded = /^([A-Za-z0-9_-]+)\*$/.exec(key);
    if (!sectioned && !wholeEncoded) {
      // Plain `name=value` — pass through unchanged.
      out[key] = value;
      continue;
    }
    const bareName = (sectioned?.[1] ?? wholeEncoded?.[1] ?? "").toLowerCase();
    const idx = sectioned ? Number(sectioned[2]) : 0;
    const encoded = sectioned ? sectioned[3] === "*" : true;
    let group = groups.get(bareName);
    if (!group) {
      group = { segments: [] };
      groups.set(bareName, group);
    }
    let segValue = value;
    // Only the first segment (idx 0) of an encoded continuation
    // carries the `charset'lang'` prefix. Strip it before storing the
    // decoded value so later joins don't include it twice.
    if (encoded && idx === 0) {
      const prefix = /^([^']*)'([^']*)'/.exec(segValue);
      if (prefix) {
        group.charset = prefix[1] || undefined;
        group.language = prefix[2] || undefined;
        segValue = segValue.slice(prefix[0].length);
      }
    }
    group.segments.push({ idx, encoded, value: segValue });
  }

  for (const [bareName, group] of groups.entries()) {
    if (out[bareName]) {
      // Plain key already won; ignore stray continuations.
      continue;
    }
    group.segments.sort((a, b) => a.idx - b.idx);
    let joined = "";
    let decodeFailed = false;
    for (const seg of group.segments) {
      if (!seg.encoded) {
        joined += seg.value;
        continue;
      }
      try {
        const decoded = decodePercentEncoded(seg.value, group.charset ?? "utf-8");
        joined += decoded;
      } catch {
        decodeFailed = true;
        joined += seg.value;
      }
    }
    if (decodeFailed) {
      defects?.push({
        code: DEFECT.HEADER_PARAM_DECODE_FAILED,
        source: "parser",
        severity: "info",
        message: `RFC 2231 parameter "${bareName}" failed to percent-decode (charset=${group.charset ?? "utf-8"}).`,
        evidence: { layer: "headers", fieldPath: `${fieldPath}.${bareName}` },
        recovery: "Used the raw (still-encoded) parameter value.",
      });
    }
    out[bareName] = joined;
    if (group.language) {
      // Language tag is informational; surface it through the param map
      // under a sibling key so callers (UI / exports) can show it.
      out[`${bareName}*lang`] = group.language;
    }
  }
  return out;
}

/**
 * RFC 2231 §4 — percent-decode a parameter segment against a charset.
 * The decoder runs the byte sequence through TextDecoder so non-UTF-8
 * charsets (e.g. iso-8859-1, gb2312) round-trip correctly.
 */
function decodePercentEncoded(value: string, charset: string): string {
  const bytes: number[] = [];
  for (let i = 0; i < value.length; i++) {
    const ch = value[i]!;
    if (ch === "%" && i + 2 < value.length) {
      const hex = value.slice(i + 1, i + 3);
      if (/^[0-9A-Fa-f]{2}$/.test(hex)) {
        bytes.push(parseInt(hex, 16));
        i += 2;
        continue;
      }
      // Not a valid percent-escape — fall through and treat the `%`
      // as a literal byte. The decoder above flags the value as
      // malformed via the surrounding try/catch.
      throw new Error("invalid percent-encoded byte");
    }
    bytes.push(ch.charCodeAt(0) & 0xff);
  }
  return decodeBytes(new Uint8Array(bytes), charset);
}

interface SplitMultipartChild {
  text: string;
  /**
   * Byte offset (inside `body`) where this child slice begins. The caller
   * adds its own offset to get an absolute source offset.
   */
  bodyOffset: number;
}

function splitMultipart(
  body: string,
  boundary: string,
  defects?: DefectCollector,
  /**
   * How many children the caller can still afford to parse. The splitter keeps
   * COUNTING past this (the count is what tells the reader how many parts were
   * skipped, and the scan is what finds the closing delimiter) but stops
   * materialising slices, because the slices are the allocation.
   *
   * Without it, containment held only for what was RETAINED: a 20 MB message
   * of ~580k tiny leaves parsed under `maxMimePartCount: 10` kept 10 nodes and
   * allocated 376 MB getting there, since every child was sliced out before
   * the first one was counted against the cap. Measured, not estimated.
   */
  maxChildren: number = Number.POSITIVE_INFINITY,
): { children: SplitMultipartChild[]; total: number } {
  const delimiter = "--" + boundary;
  const parts: SplitMultipartChild[] = [];
  let total = 0;
  /** Push a child only while the caller can still use one; always count. */
  const emit = (text: () => string, bodyOffset: number) => {
    if (total < maxChildren) parts.push({ text: text(), bodyOffset });
    total++;
  };
  let current: string[] | null = null;
  let currentOffset = 0;
  let sawClosing = false;
  let sawAnyOpening = false;

  // Scanned with indexOf rather than `body.split("\n")`. The split materialised
  // EVERY line of the body at once and held them all live for the duration:
  // that array, not the child slices, was the bulk of the 376 MB a 20 MB
  // adversarial multipart allocated. Here one line is live at a time, and a
  // line that is neither a delimiter nor part of a wanted child is never even
  // sliced out.
  //
  // `lineOffset` tracks the byte offset of the start of each line inside
  // `body`, derived from `lineLen + 1` (the consumed newline) so the offsets
  // stay aligned even where trailing whitespace is collapsed.
  let lineOffset = 0;
  let pos = 0;
  while (pos <= body.length) {
    let nl = body.indexOf("\n", pos);
    if (nl === -1) nl = body.length;
    const lineLen = nl - pos;
    // MIME-003. RFC 2046 §5.1.1 only allows `transport-padding`
    // (`*LWSP-char`, i.e. SPACE / HTAB) between the boundary delimiter and
    // the CRLF, so only space + tab are stripped before the comparison: a line
    // ending in e.g. `\v` must NOT read as a clean delimiter. (catalog E8:
    // senders pad the closing `--boundary--` with extra spaces / tabs; that is
    // still tolerated.) Done by moving an index rather than with a regex, so a
    // non-delimiter line costs no allocation at all.
    let end = nl;
    while (end > pos && (body[end - 1] === " " || body[end - 1] === "\t")) end--;
    const trimmedLen = end - pos;
    const isOpening = trimmedLen === delimiter.length && body.startsWith(delimiter, pos);
    const isClosing =
      trimmedLen === delimiter.length + 2 &&
      body.startsWith(delimiter, pos) &&
      body.startsWith("--", pos + delimiter.length);
    if (isOpening) {
      if (current !== null) {
        emit(() => current!.join("\n"), currentOffset);
      }
      current = [];
      // The child's body begins on the next line — after the boundary
      // line + its newline.
      currentOffset = lineOffset + lineLen + 1;
      sawAnyOpening = true;
    } else if (isClosing) {
      if (current !== null) {
        emit(() => current!.join("\n"), currentOffset);
      }
      sawClosing = true;
      break;
    } else if (current !== null && total < maxChildren) {
      // Past the budget the child will never be parsed, so its lines are dead
      // weight: keep scanning for the delimiter, accumulate nothing.
      current.push(body.slice(pos, nl));
    }
    lineOffset += lineLen + 1;
    pos = nl + 1;
  }

  // If we entered a part but never saw the closing `--boundary--`, the
  // message is structurally incomplete. Push a warning defect (this is
  // already a user-visible class of failure — the MIME tree may be missing
  // its tail) but keep whatever parts we did manage to slice out.
  if (sawAnyOpening && !sawClosing) {
    if (current !== null && current.length > 0) {
      // Salvage the trailing part — without a closing delimiter we
      // would otherwise drop everything after the last opener.
      emit(() => current!.join("\n"), currentOffset);
    }
    defects?.push({
      code: DEFECT.MIME_BOUNDARY_UNCLOSED,
      source: "parser",
      severity: "warning",
      message: `multipart with boundary "${boundary}" never saw its closing "--${boundary}--" delimiter.`,
      evidence: { layer: "mime", fieldPath: "root" },
      recovery: "Salvaged the partial parts up to EOF.",
    });
  } else if (!sawAnyOpening) {
    // A multipart container that never produced a single opening boundary
    // line is fully malformed. Emit a warning-class defect so the Defects tab
    // surfaces it.
    defects?.push({
      code: DEFECT.MIME_BOUNDARY_MISSING,
      source: "parser",
      severity: "warning",
      message: `multipart with boundary "${boundary}" had no opening delimiter; tree will be empty.`,
      evidence: { layer: "mime", fieldPath: "root" },
      recovery: "Returned an empty parts list; container metadata still surfaces.",
    });
  }

  // Trim a leading `\n` from each slice (the boundary is followed by
  // a newline before the child's headers start). The trim also
  // shifts the offset by 1 so the caller's offset arithmetic still
  // points at the first byte of the child's headers.
  const children = parts.map((p) => {
    let text = p.text;
    let bodyOffset = p.bodyOffset;
    if (text.startsWith("\n")) {
      text = text.slice(1);
      bodyOffset += 1;
    }
    if (text.endsWith("\n")) {
      text = text.slice(0, -1);
    }
    return { text, bodyOffset };
  });
  return { children, total };
}

// ---------- Body decoding ----------

function decodeTransferEncoding(
  body: string,
  encoding: string,
  defects?: DefectCollector,
): Uint8Array {
  switch (encoding) {
    case "base64":
      return decodeBase64Bytes(body, defects);
    case "quoted-printable":
      return decodeQuotedPrintableBytes(body, defects);
    case "7bit":
    case "8bit":
    case "binary":
    case "":
      return rawStringToBytes(body);
    default: {
      // ENCODING_UNKNOWN. CTE header named a value that isn't any of the
      // recognised RFC 2045 encodings (`x-uuencode`, `gzip`, `x-gzip`, …).
      // The parser falls back to passing the bytes through verbatim so the
      // user still sees *something*.
      defects?.push({
        code: DEFECT.ENCODING_UNKNOWN,
        source: "parser",
        severity: "warning",
        message: `Content-Transfer-Encoding "${encoding}" is not a recognised RFC 2045 encoding.`,
        evidence: { layer: "mime", fieldPath: "part.transferEncoding" },
        recovery: "Treated the body as 7bit / 8bit raw bytes.",
      });
      return rawStringToBytes(body);
    }
  }
}

function rawStringToBytes(input: string): Uint8Array {
  // Delegates to the shared byte-string→octets helper; kept as a named
  // local so the parser's call sites read in domain terms.
  return byteStringToBytes(input);
}

function decodeBase64Bytes(input: string, defects?: DefectCollector): Uint8Array {
  // Interspersed whitespace (CRLF + tabs + spaces) is expected in real-world
  // base64 streams. We strip it cleanly here so the decoder sees a contiguous
  // payload and emits no defect on a successful decode.
  const cleaned = input.replace(/\s+/g, "");
  if (!cleaned) return new Uint8Array(0);
  // ENCODING_BASE64_INVALID_CHAR. Distinct from
  // ENCODING_BASE64_INVALID_PADDING (atob's complaint): this is the "bytes
  // outside the base64 alphabet" case (chars like `~`, `?`, `*` interleaved
  // with valid base64 chars). We sniff before atob so the diagnostic
  // distinguishes "bad alphabet" from "bad padding" — both are pushed when
  // both apply.
  if (/[^A-Za-z0-9+/=]/.test(cleaned)) {
    defects?.push({
      code: DEFECT.ENCODING_BASE64_INVALID_CHAR,
      source: "parser",
      severity: "info",
      message:
        "Base64 payload contains characters outside the A-Z a-z 0-9 + / = alphabet.",
      evidence: { layer: "mime", fieldPath: "part.bytes" },
      recovery: "Decode still attempted; invalid bytes may cause padding failure.",
    });
  }
  try {
    const binary = atob(cleaned);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    // Expected: input is not valid base64 (corrupt, line-broken with odd
    // padding, or simply 7-bit text that the sender mislabelled as
    // base64). Per the permissive parser contract, fall back to the raw
    // bytes so the user still sees *something*.
    defects?.push({
      code: DEFECT.ENCODING_BASE64_INVALID_PADDING,
      source: "parser",
      severity: "info",
      message:
        "Base64 payload failed to decode (likely invalid padding or stray characters).",
      evidence: { layer: "mime", fieldPath: "part.bytes" },
      recovery: "Surfaced raw bytes byte-for-byte so consumers still see something.",
    });
    return rawStringToBytes(input);
  }
}

function decodeQuotedPrintableBytes(
  input: string,
  defects?: DefectCollector,
): Uint8Array {
  // Soft line breaks (`=\r\n` / `=\n`) drop out cleanly before we look at the
  // rest of the body. They are spec-compliant RFC 2045 §6.7 wrappers and emit
  // no defect.
  const joined = input.replace(/=\r?\n/g, "").replace(/=\n/g, "");
  // Pre-size to the joined length (the upper bound — every decoded `=XX`
  // escape collapses 3 source chars into 1 byte, so output is never longer)
  // and track the write cursor, avoiding the per-byte number[] push churn.
  const out = new Uint8Array(joined.length);
  let n = 0;
  let invalidEscapes = 0;
  for (let i = 0; i < joined.length; i++) {
    // Bounded by `i < joined.length` so the read is in-range.
    const ch = joined[i]!;
    if (ch === "=" && i + 2 < joined.length) {
      const hex = joined.slice(i + 1, i + 3);
      if (/^[0-9A-Fa-f]{2}$/.test(hex)) {
        out[n++] = parseInt(hex, 16);
        i += 2;
        continue;
      }
      // `=` followed by something that is neither a soft-break nor two hex
      // digits. Surface as info defect (one per body, not per byte; the tally
      // below collapses into a single emission).
      invalidEscapes++;
    }
    out[n++] = ch.charCodeAt(0) & 0xff;
  }
  if (invalidEscapes > 0) {
    defects?.push({
      code: DEFECT.ENCODING_QP_INVALID_ESCAPE,
      source: "parser",
      severity: "info",
      message: `Quoted-printable body had ${invalidEscapes} '=' followed by non-hex / non-soft-break sequences.`,
      evidence: { layer: "mime", fieldPath: "part.body" },
      recovery: "Treated each malformed '=' as a literal byte.",
    });
  }
  return out.subarray(0, n);
}

function decodeBytes(
  bytes: Uint8Array,
  charset: string,
  defects?: DefectCollector,
): string {
  const label = normalizeCharset(charset);
  try {
    // CHARSET_DECODE_FAILED. We attempt a strict (`fatal: true`) decode first
    // so any invalid byte sequence (mid-rune truncation, overlong encoding,
    // lone surrogate) is detected. On failure we surface the formal defect
    // and re-decode permissively so the user still sees something.
    if (defects && bytes.byteLength > 0) {
      try {
        new TextDecoder(label, { fatal: true }).decode(bytes);
      } catch {
        defects.push({
          code: DEFECT.CHARSET_DECODE_FAILED,
          source: "parser",
          severity: "info",
          message: `Charset "${charset}" decoder rejected the bytes as invalid for the declared encoding.`,
          evidence: { layer: "mime", fieldPath: "part.bytes" },
          recovery:
            "Re-decoded with fatal=false; replacement characters (U+FFFD) substituted.",
        });
      }
    }
    return new TextDecoder(label, { fatal: false }).decode(bytes);
  } catch {
    // Charset label not recognised by this runtime (rare legacy encodings or
    // a typo in the header). Emit CHARSET_UNKNOWN severity info and fall back
    // to UTF-8 so the user still sees decoded text.
    defects?.push({
      code: DEFECT.CHARSET_UNKNOWN,
      source: "parser",
      severity: "info",
      message: `Charset label "${charset}" not recognised by the runtime; falling back to UTF-8.`,
      evidence: { layer: "mime", fieldPath: "part.charset" },
      recovery: "Decoded with UTF-8.",
    });
    try {
      return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    } catch {
      // …and finally to byte-by-byte Latin-1 if even UTF-8 isn't
      // available (extremely old engines / TextDecoder polyfill missing).
      let out = "";
      for (const b of bytes) out += String.fromCharCode(b);
      return out;
    }
  }
}

function normalizeCharset(charset: string): string {
  const c = charset.toLowerCase().replace(/^"|"$/g, "");
  if (c === "us-ascii" || c === "ascii") return "utf-8";
  if (c === "cp1252") return "windows-1252";
  return c || "utf-8";
}

// ---------- RFC 2047 encoded-word header decoding ----------

/**
 * Decode RFC 2047 encoded-word atoms (`=?charset?B?...?=` /
 * `=?charset?Q?...?=`) embedded in a header value. Call on any header
 * that may contain non-ASCII display text — Subject, From, To, filenames
 * in Content-Disposition. Unrecognised charsets fall back to UTF-8.
 *
 * Pure / no-side-effects shape — every public caller (HeaderRow,
 * useExports, derived analysis) keeps using this signature. The
 * defect-aware variant {@link decodeEncodedWordWithDefects} is used
 * inside `parseEmail` only.
 */
export function decodeEncodedWord(value: string): string {
  return decodeEncodedWordWithDefects(value, undefined);
}

/**
 * Same as {@link decodeEncodedWord}, but pushes a {@link Defect} of code
 * `HEADER_DECODE_FAILED` (severity `info`) onto the collector for any
 * encoded-word atom that hits an unsupported shape:
 *
 * - Empty payload (`=?utf-8?B??=`) — produces nothing useful. - Mixed
 * encodings within a single atom — e.g. base64 followed immediately by
 * quoted-printable joined by whitespace; we can decode each piece, but the
 * spec discourages mixing. - Language tag (`=?utf-8*en?B?…?=`) — RFC 2231 §5;
 * we honour the charset prefix, drop the lang suffix, and note the recovery.
 *
 * The recovered string is whatever the legacy decoder produced — the defect
 * is purely an informative trail for the Defects tab.
 */
export function decodeEncodedWordWithDefects(
  value: string,
  defects: DefectCollector | undefined,
  fieldPath = "headers",
): string {
  if (!value) return value;
  // RFC 2047 §2 forbids whitespace in the encoded-text portion. The legacy
  // regex used `[^?]*` for the payload, so `=?utf-8?Q?Hello world?=` was
  // decoded — which is exactly the shape §2 calls out as four atoms, NOT an
  // encoded-word. Pre-scan the value with the *loose* regex and emit an info
  // defect for any atom whose payload carries whitespace; the strict regex
  // below then refuses to match it.
  emitWhitespaceInPayloadDefect(value, defects, fieldPath);
  // RFC 2047 §2 caps each encoded-word at 75 chars (including the surrounding
  // `=?…?=`). The decoder still proceeds past this point — the limit is an
  // interop guideline, not a hard-fail — but the Defects tab gains an info
  // trail.
  emitOversizedEncodedWordDefect(value, defects, fieldPath);
  // RFC 2047 §6.2 — adjacent encoded-words separated only by whitespace
  // are concatenated *without* the whitespace. Glue them up here so the
  // decoder treats them as one chain.
  const collapsed = value.replace(/\?=\s+=\?/g, "?==?");
  // Strict payload (`[^?\s]*`) so whitespace-bearing payloads are not even
  // recognised as encoded words; leading separator
  // (`(?<=^|\s|\?=|[()])`) so an encoded-word glued to adjacent text
  // (`Hello=?utf-8?B?V29ybGQ=?=foo`) doesn't decode, while one that abuts a
  // comment paren still does. RFC 2047 §5(2) permits an encoded-word inside a
  // `comment`, where `(` / `)` are the delimiters rather than FWS — e.g.
  // `From: (=?utf-8?q?=C3=A9?=) foo@bar`. The trailing `)` needs no handling
  // since the pattern already ends at `?=`.
  const pattern = /(?<=^|\s|\?=|[()])=\?([^?]+)\?([BQbq])\?([^?\s]*)\?=/g;
  // Resolve every encoded-word atom to its decode metadata, emitting the
  // per-atom defects (especials, empty payload, language tag) in document
  // order exactly as the legacy per-match decoder did.
  const atoms: EncodedWordAtom[] = [];
  for (const m of collapsed.matchAll(pattern)) {
    const rawCharset = m[1] ?? "";
    const encoding = m[2] ?? "";
    const payload = m[3] ?? "";
    const start = m.index ?? 0;
    // EW-007. RFC 2047 §2 says the charset token is `1*<Any CHAR except
    // SPACE, CTLs, and especials>` where `especials = "(" / ")" / "<" / ">"
    // / "@" / "," / ";" / ":" / <"> / "/" / "[" / "]" / "?" / "." / "=" /
    // SPACE`. A charset containing one of those characters is malformed;
    // emit an info defect (the decoder still tries to honour it —
    // TextDecoder will fall back to UTF-8 if the runtime doesn't know the
    // label). The lang-tagged form (`utf-8*en`) is handled below and stays
    // compliant.
    const especialsRe = /[()<>@,;:"/[\]?.=\s]/;
    const charsetForValidation = rawCharset.split("*", 1)[0] ?? rawCharset;
    if (especialsRe.test(charsetForValidation)) {
      defects?.push({
        code: DEFECT.HEADER_DECODE_FAILED,
        source: "parser",
        severity: "info",
        message: `RFC 2047 charset "${rawCharset}" contains an "especials" character (RFC 2047 §2).`,
        evidence: { layer: "headers", fieldPath },
        recovery: "Decoder still attempted with the charset as labelled.",
      });
    }
    // E2.empty — payload is empty; emit info defect, decode to nothing. An
    // empty atom carries no octets, so it cannot join a multibyte run and
    // breaks any surrounding octet group.
    if (payload === "") {
      defects?.push({
        code: DEFECT.HEADER_DECODE_FAILED,
        source: "parser",
        severity: "info",
        message: `Empty RFC 2047 encoded-word payload (charset=${rawCharset}).`,
        evidence: { layer: "headers", fieldPath },
        recovery: "Replaced encoded-word atom with empty string.",
      });
      atoms.push({
        start,
        end: start + m[0].length,
        rawCharset,
        encoding,
        payload,
        empty: true,
      });
      continue;
    }
    // E2.lang — RFC 2231 §5 charset*language form. Strip the *xx suffix,
    // decode against the bare charset, but record the recovery so the UI
    // can surface it.
    let charset = rawCharset;
    const langMatch = /^([^*]+)\*([A-Za-z0-9-]+)$/.exec(rawCharset);
    if (langMatch) {
      charset = langMatch[1] ?? rawCharset;
      defects?.push({
        code: DEFECT.HEADER_DECODE_FAILED,
        source: "parser",
        severity: "info",
        message: `RFC 2047 language tag "${langMatch[2]}" on encoded-word ignored.`,
        evidence: { layer: "headers", fieldPath },
        recovery: `Decoded with charset "${charset}"; language tag dropped.`,
      });
    }
    atoms.push({
      start,
      end: start + m[0].length,
      rawCharset,
      encoding,
      payload,
      charset,
      empty: false,
    });
  }
  if (atoms.length === 0) return collapsed;
  // RFC 2047 §6.2 — when adjacent encoded-words are displayed they MUST be
  // concatenated at the *octet* level before charset-decoding, because a
  // multibyte character may straddle the word boundary
  // (`=?utf-8?B?4oI=?= =?utf-8?B?rA==?=` is the euro sign E2 82 AC split as
  // base64(E2 82) + base64(AC)). Coalesce maximal runs of consecutive atoms
  // that are textually adjacent (the §6.2 separating whitespace was already
  // collapsed above) AND agree on encoding letter + normalised charset,
  // decode each payload to bytes, concatenate the Uint8Arrays, then run the
  // charset decoder once per run. Empty atoms and any charset/encoding change
  // start a fresh run, so clean-boundary chains keep their prior result.
  let out = "";
  let cursor = 0;
  let i = 0;
  while (i < atoms.length) {
    const head = atoms[i]!;
    // Copy any literal text between the previous atom and this one verbatim.
    out += collapsed.slice(cursor, head.start);
    if (head.empty) {
      cursor = head.end;
      i += 1;
      continue;
    }
    // Extend the run while the next atom is adjacent, non-empty, and agrees on
    // encoding letter + charset. RFC 2047 §6.2 sanctions octet-level
    // concatenation ONLY for encoded-words of the SAME charset, so compare the
    // RAW charset token (case-insensitively — charset names are
    // case-insensitive, so "UTF-8"/"utf-8" still merge), NOT the alias-folded
    // charset. Folding first would byte-merge distinct labels that share a
    // normalised alias (us-ascii↔utf-8, cp1252↔windows-1252) — a literal spec
    // deviation that can fabricate a codepoint across a charset boundary. The
    // decode below still normalises the head charset for TextDecoder.
    const encUpper = head.encoding.toUpperCase();
    const rawCharsetKey = head.charset!.trim().toLowerCase();
    let j = i;
    while (
      j + 1 < atoms.length &&
      !atoms[j + 1]!.empty &&
      atoms[j + 1]!.start === atoms[j]!.end &&
      atoms[j + 1]!.encoding.toUpperCase() === encUpper &&
      atoms[j + 1]!.charset!.trim().toLowerCase() === rawCharsetKey
    ) {
      j += 1;
    }
    out += decodeEncodedWordRun(
      atoms.slice(i, j + 1),
      encUpper,
      head.charset!,
      defects,
      fieldPath,
    );
    cursor = atoms[j]!.end;
    i = j + 1;
  }
  out += collapsed.slice(cursor);
  return out;
}

/** One resolved RFC 2047 encoded-word atom, ready for octet-level grouping. */
interface EncodedWordAtom {
  /** Offset of the atom within the whitespace-collapsed header value. */
  start: number;
  /** Offset just past the atom's closing `?=`. */
  end: number;
  /** Charset token exactly as written (lang tag, if any, still attached). */
  rawCharset: string;
  /** Encoding letter as written (`B` / `Q` / `b` / `q`). */
  encoding: string;
  /** The `?…?` encoded-text payload. */
  payload: string;
  /** Charset with any RFC 2231 `*lang` suffix stripped; absent when empty. */
  charset?: string;
  /** Empty-payload atom — decodes to "" and breaks octet runs. */
  empty: boolean;
}

/**
 * Decode one run of adjacent same-charset/same-encoding encoded-words at the
 * octet level (RFC 2047 §6.2): each payload is turned into bytes, the byte
 * buffers are concatenated, and the charset decoder runs once over the whole
 * run so a multibyte character split across the word boundary is recovered.
 *
 * Mirrors the legacy per-atom decode-failure recovery: on a throw the raw
 * payloads are surfaced and one info defect is emitted per atom in the run.
 */
function decodeEncodedWordRun(
  run: EncodedWordAtom[],
  encUpper: string,
  charset: string,
  defects: DefectCollector | undefined,
  fieldPath: string,
): string {
  try {
    const chunks = run.map((atom) =>
      encUpper === "B"
        ? decodeBase64Bytes(atom.payload)
        : decodeQuotedPrintableBytes(atom.payload.replace(/_/g, " ")),
    );
    const total = chunks.reduce((sum, c) => sum + c.length, 0);
    const merged = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    return decodeBytes(merged, charset);
  } catch {
    // Expected: malformed encoded-word payload. Surface the raw payloads
    // rather than blow up the whole header value — the user still sees
    // *something* legible — and emit one info defect per atom, matching the
    // legacy per-atom emission count.
    for (const atom of run) {
      defects?.push({
        code: DEFECT.HEADER_DECODE_FAILED,
        source: "parser",
        severity: "info",
        message: `RFC 2047 encoded-word decode failed (charset=${atom.rawCharset}, encoding=${encUpper}).`,
        evidence: { layer: "headers", fieldPath },
        recovery: "Surfaced the raw payload string.",
      });
    }
    return run.map((atom) => atom.payload).join("");
  }
}

/**
 * Detect mixed-encoding adjacent encoded-words within a single header value.
 * Two consecutive `=?…?=` atoms that disagree on the encoding letter (`B` vs
 * `Q`) are technically legal but uncommon; we surface a single info defect
 * per header so the Defects tab can flag the value for inspection.
 */
function detectMixedEncodings(
  value: string,
  defects: DefectCollector | undefined,
  fieldPath: string,
): void {
  if (!defects) return;
  const atoms = Array.from(value.matchAll(/=\?[^?]+\?([BQbq])\?[^?]*\?=/g));
  if (atoms.length < 2) return;
  const encodings = new Set(atoms.map((m) => (m[1] ?? "").toUpperCase()));
  if (encodings.size > 1) {
    defects.push({
      code: DEFECT.HEADER_DECODE_FAILED,
      source: "parser",
      severity: "info",
      message: "RFC 2047 mixed encodings (B + Q) within a single header value.",
      evidence: { layer: "headers", fieldPath },
      recovery: "Each atom decoded independently with its own encoding.",
    });
  }
}

/**
 * RFC 2047 §2 forbids whitespace inside the encoded-text portion of an
 * encoded-word. Walk every `=?…?Q?…?=` / `=?…?B?…?=`-shaped atom in the
 * header value with a loose regex (the one matching the *broken* shape on the
 * wire) and emit `HEADER_DECODE_FAILED` info when its payload carries any
 * whitespace. The strict decoder regex skips the same atom, so the
 * post-decode text preserves the on-wire bytes (the original four-atom view
 * per §2's example).
 */
function emitWhitespaceInPayloadDefect(
  value: string,
  defects: DefectCollector | undefined,
  fieldPath: string,
): void {
  if (!defects) return;
  const looseAtom = /=\?[^?\s]+\?[BQbq]\?([^?]*)\?=/g;
  for (const m of value.matchAll(looseAtom)) {
    const payload = m[1] ?? "";
    if (/\s/.test(payload)) {
      defects.push({
        code: DEFECT.HEADER_DECODE_FAILED,
        source: "parser",
        severity: "info",
        message:
          "RFC 2047 §2 — encoded-word contains whitespace in its encoded-text payload; rejected.",
        evidence: { layer: "headers", fieldPath },
        recovery: "Atom kept verbatim; downstream sees the raw =?…?= token.",
      });
      // One emission per header is enough — the user just needs to know
      // the value carries a non-conformant encoded-word.
      return;
    }
  }
}

/**
 * RFC 2047 §2 caps every encoded-word at 75 chars total (`=?…?=` inclusive).
 * The decoder still proceeds when the limit is exceeded — the cap is an
 * interop guideline, not a hard fail — but the Defects tab gains an info
 * trail so the operator can inspect the offending header.
 */
function emitOversizedEncodedWordDefect(
  value: string,
  defects: DefectCollector | undefined,
  fieldPath: string,
): void {
  if (!defects) return;
  const atomRe = /=\?[^?]+\?[BQbq]\?[^?]*\?=/g;
  for (const m of value.matchAll(atomRe)) {
    if (m[0].length > 75) {
      defects.push({
        code: DEFECT.HEADER_DECODE_FAILED,
        source: "parser",
        severity: "info",
        message: `RFC 2047 §2 — encoded-word length ${m[0].length} exceeds the 75-character cap.`,
        evidence: { layer: "headers", fieldPath },
        recovery: "Atom still decoded; consumers SHOULD line-wrap on send.",
      });
      return;
    }
  }
}

// ---------- Helpers ----------

function walk(part: EmailPart, visit: (p: EmailPart) => void): void {
  visit(part);
  for (const child of part.parts) walk(child, visit);
}

/**
 * Apply RFC 2646 / RFC 3676 unfolding to the chosen text/plain part when its
 * content-type opts into `format=flowed`. Returns the wire-faithful body
 * otherwise so any consumer relying on byte-for-byte equality with
 * `part.body` keeps working.
 */
function decodeFlowedBody(part: EmailPart): string {
  // `part.contentType` is the bare media type ("text/plain") — the format /
  // delsp parameters were split off into the part's `format` / `delsp` fields
  // at parse time, so read them directly instead of re-parsing the media type
  // (which never carries them, leaving the feature permanently dead).
  const flowed = part.format === "flowed";
  if (!flowed) return part.body;
  const delsp = part.delsp === "yes";
  return unfoldFormatFlowed(part.body, { delsp });
}

function findBodyPart(part: EmailPart, type: string): EmailPart | null {
  if (part.contentType === type && part.disposition !== "attachment" && !part.filename) {
    return part;
  }
  for (const child of part.parts) {
    const hit = findBodyPart(child, type);
    if (hit) return hit;
  }
  return null;
}

function isAttachmentPart(
  part: EmailPart,
  htmlBody: EmailPart | null,
  textBody: EmailPart | null,
): boolean {
  if (part.parts.length > 0) return false;
  if (part === htmlBody || part === textBody) return false;
  if (part.disposition === "attachment") return true;
  if (part.filename) return true;
  if (part.contentId) return true;
  // Any non-text, non-multipart leaf with actual bytes is probably an
  // attachment even if headers are sparse.
  if (!part.contentType.startsWith("text/") && part.bytes.byteLength > 0) {
    return true;
  }
  return false;
}

function defaultFilename(part: EmailPart): string {
  const ext = guessExtension(part.contentType);
  const base = part.contentId?.replace(/[^a-z0-9._-]/gi, "_") ?? "attachment";
  return ext && !base.endsWith(ext) ? `${base}${ext}` : base;
}

function guessExtension(contentType: string): string {
  const map: Record<string, string> = {
    "application/pdf": ".pdf",
    "application/zip": ".zip",
    "application/json": ".json",
    "image/png": ".png",
    "image/jpeg": ".jpg",
    "image/gif": ".gif",
    "image/webp": ".webp",
    "image/svg+xml": ".svg",
    "text/csv": ".csv",
    "text/calendar": ".ics",
  };
  return map[contentType] ?? "";
}

function stripAngleBrackets(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return value.trim().replace(/^<|>$/g, "");
}

function extractNamedHeaders(
  headers: Record<string, string>,
  all: HeaderEntry[],
  defects?: DefectCollector,
): ParsedHeaders {
  const decode = (v: string | undefined, fieldPath: string) => {
    if (!v) return undefined;
    detectMixedEncodings(v, defects, fieldPath);
    return decodeEncodedWordWithDefects(v, defects, fieldPath);
  };
  // RFC 2047 §5 applies to *all* header fields that carry display text, not
  // just From/To/Cc/Subject. Run the mixed-encoding detector over every
  // header whose value contains an encoded-word atom — Reply-To, Sender,
  // Resent-*, X-* and any future field gain coverage without touching this
  // list again.
  if (defects) {
    const named = new Set(["from", "to", "cc", "subject"]);
    for (const h of all) {
      const lower = h.name.toLowerCase();
      if (named.has(lower)) continue;
      if (!h.value || !h.value.includes("=?")) continue;
      detectMixedEncodings(h.value, defects, `headers.${lower}`);
    }
  }
  return {
    raw: headers,
    all,
    from: decode(headers["from"], "headers.from"),
    to: decode(headers["to"], "headers.to"),
    cc: decode(headers["cc"], "headers.cc"),
    subject: decode(headers["subject"], "headers.subject"),
    date: headers["date"],
    messageId: headers["message-id"],
  };
}
